// Snowflake folds unquoted identifiers to upper case; these lower-case physical
// column names match the upper-case stored columns and are not English words.
// cspell:ignore finishedat scheduledat eventtype costcarrierfeemicros teamid automationid campaignid sendat

export const SemanticTable = {
  CheckoutWidgets: "checkout_widgets",
  CheckoutWidgetAttributedOrderLineItems:
    "checkout_widget_attributed_order_line_items",
  OrderLineItemProperties: "order_line_item_properties",
  FulfillmentGroups: "fulfillment_groups",
  FulfillmentGroupLineItems: "fulfillment_group_line_items",
  FulfillmentGroupShipments: "fulfillment_group_shipments",
  FulfillmentGroupShipmentSurcharges: "fulfillment_group_shipment_surcharges",
  FulfillmentGroupFulfillmentOrders: "fulfillment_group_fulfillment_orders",
  Orders: "orders",
  OrderLineItems: "order_line_items",
  /**
   * Second view of the order tables, reached only from a return. Separate
   * tables, not extra `orders` edges: see AnalyticsRelationshipName.
   */
  ExchangeOrders: "exchange_orders",
  ReturnLineItems: "return_line_items",
  OrderFulfillments: "order_fulfillments",
  OrderFulfillmentLineItems: "order_fulfillment_line_items",
  ReturnShipmentTrackingDetails: "return_shipment_tracking_details",
  ReturnLineItemMultipleChoice: "return_line_item_multiple_choice",
  Returns: "returns",
  ReturnPaymentIntents: "return_payment_intents",
  ReturnShipments: "return_shipments",
  MarketingOutreachEvents: "marketing_outreach_events",
  Campaigns: "campaigns",
  Automations: "automations",
  CheckoutVisits: "checkout_visits",
  CheckoutVisitAdvanced: "checkout_visit_advanced",
  CheckoutVisitTreatments: "checkout_visit_treatments",
  Treatments: "treatments",
  Splits: "splits",
  OrderShippingLines: "order_shipping_lines",
  OrderRefundLineItems: "order_refund_line_items",
  PostPurchaseUpsellEvents: "post_purchase_upsell_events",
  PostPurchaseUpsellPages: "post_purchase_upsell_pages",
  Products: "products",
  InventoryLevels: "inventory_levels",
  StorageLocations: "storage_locations",
  Locations: "locations",
  Bills: "bills",
  BillingCharges: "billing_charges",
  RescuePanelMetrics: "rescue_panel_metrics",
  RescuePanelAttributedOrders: "rescue_panel_attributed_orders",
  ShippingClaims: "shipping_claims",
  DraftReturnPendingReturnItems: "draft_return_pending_return_items",
  Conversations: "conversations",
  ConversationMessages: "conversation_messages",
  ReturnTags: "return_tags",
  SatisfactionResponses: "satisfaction_responses",
  OrderExtendedWarranties: "order_extended_warranties",
  FailedDiscountRescueEvents: "failed_discount_rescue_events",
  CustomerTags: "customer_tags",
  CustomCustomerEvents: "custom_customer_events",
  CustomerCustomFields: "customer_custom_fields",
  ProductTags: "product_tags",
  FulfillmentGroupTrackingEvents: "fulfillment_group_tracking_events",
  ProductCollections: "product_collections",
  ProductReviews: "product_reviews",
  DailyShopperSessions: "daily_shopper_sessions",
  MarketingAgentDecisions: "marketing_agent_decisions",
  MarketingAgentDecisionTags: "marketing_agent_decision_tags",
  CustomerGroupMemberships: "customer_group_memberships",
  CampaignRecipients: "campaign_recipients",
  CheckoutDeliveryOptions: "checkout_delivery_options",
  CheckoutDeliveryGroups: "checkout_delivery_groups",
  CheckoutInfoChanges: "checkout_info_changes",
  CheckoutShippingStepEvents: "checkout_shipping_step_events",
  CheckoutOrderCreatedEvents: "checkout_order_created_events",
  CheckoutWidgetInteractions: "checkout_widget_interactions",
  PostPurchaseSplitAssignments: "post_purchase_split_assignments",
  MarketingSubscriptionEvents: "marketing_subscription_events",
  BackInStockEvents: "back_in_stock_events",
  CollectionViewEvents: "collection_view_events",
  ProductViewEvents: "product_view_events",
  StorefrontCheckoutEvents: "storefront_checkout_events",
  CartChangeEvents: "cart_change_events",
  TryFirstPlans: "try_first_plans",
  TryFirstContracts: "try_first_contracts",
  ReturnAssessmentResponses: "return_assessment_responses",
  ReturnAssessmentDefinitionFields: "return_assessment_definition_fields",
  ReturnAssessments: "return_assessments",
  TrackingPageEvents: "tracking_page_events",
  OrderTrackingPageOfferClicks: "order_tracking_page_offer_clicks",
  OrderTrackingEmailEvents: "order_tracking_email_events",
  OrderTrackingDetails: "order_tracking_details",
  InboundMessages: "inbound_messages",
  MercuryShippingLines: "mercury_shipping_lines",
  OrderPaymentGatewayNames: "order_payment_gateway_names",
  Integrations: "integrations",
  Customers: "customers",
  SupportTickets: "support_tickets",
  SupportTicketMessages: "support_ticket_messages",
  MessageTopicLinks: "message_topic_links",
  SupportTicketTopics: "support_ticket_topics",
} as const;

export type SemanticTableName =
  (typeof SemanticTable)[keyof typeof SemanticTable];

type SemanticTableMap<TValue> = {
  readonly [TTable in SemanticTableName]: TValue;
};

function defineSemanticTableMap<const TMap extends SemanticTableMap<unknown>>(
  map: TMap & Record<Exclude<keyof TMap, SemanticTableName>, never>,
): TMap {
  return map;
}

export const SemanticTableLabel = defineSemanticTableMap({
  [SemanticTable.CheckoutWidgets]: "Checkout Widgets",
  [SemanticTable.CheckoutWidgetAttributedOrderLineItems]:
    "Checkout Widget Attributed Order Line Items",
  [SemanticTable.OrderLineItemProperties]: "Order Line Item Properties",
  [SemanticTable.FulfillmentGroups]: "Fulfillment Groups",
  [SemanticTable.FulfillmentGroupLineItems]: "Fulfillment Group Line Items",
  [SemanticTable.FulfillmentGroupShipments]: "Fulfillment Group Shipments",
  [SemanticTable.FulfillmentGroupShipmentSurcharges]:
    "Fulfillment Group Shipment Adjustments",
  [SemanticTable.FulfillmentGroupFulfillmentOrders]:
    "Fulfillment Group Fulfillment Orders",
  [SemanticTable.Orders]: "Orders",
  [SemanticTable.OrderLineItems]: "Order Line Items",
  [SemanticTable.ExchangeOrders]: "Exchange Orders",
  [SemanticTable.ReturnLineItems]: "Return Line Items",
  [SemanticTable.OrderFulfillments]: "Order Fulfillments",
  [SemanticTable.OrderFulfillmentLineItems]: "Order Fulfillment Line Items",
  [SemanticTable.ReturnShipmentTrackingDetails]:
    "Return Shipment Tracking Details",
  [SemanticTable.ReturnLineItemMultipleChoice]:
    "Return Multiple Choice Answers",
  [SemanticTable.Returns]: "Returns",
  [SemanticTable.ReturnPaymentIntents]: "Return Payment Intents",
  [SemanticTable.ReturnShipments]: "Return Shipments",
  [SemanticTable.MarketingOutreachEvents]: "Marketing Outreach Events",
  [SemanticTable.Campaigns]: "Campaigns",
  [SemanticTable.Automations]: "Automations",
  [SemanticTable.CheckoutVisits]: "Checkout Visits",
  [SemanticTable.CheckoutVisitAdvanced]: "Checkout Visit Advanced",
  [SemanticTable.CheckoutVisitTreatments]: "Checkout Visit Treatments",
  [SemanticTable.Treatments]: "Treatments",
  [SemanticTable.Splits]: "A/B Tests",
  [SemanticTable.OrderShippingLines]: "Order Shipping Lines",
  [SemanticTable.OrderRefundLineItems]: "Order Refund Line Items",
  [SemanticTable.PostPurchaseUpsellEvents]: "Post-Purchase Upsell Events",
  [SemanticTable.PostPurchaseUpsellPages]: "Post-Purchase Upsell Pages",
  [SemanticTable.Products]: "Products",
  [SemanticTable.InventoryLevels]: "Inventory Levels",
  [SemanticTable.StorageLocations]: "Storage Locations",
  [SemanticTable.Locations]: "Locations",
  [SemanticTable.Bills]: "Bills",
  [SemanticTable.BillingCharges]: "Billing Charges",
  [SemanticTable.RescuePanelMetrics]: "Rescue Panel Metrics",
  [SemanticTable.RescuePanelAttributedOrders]: "Rescue Panel Attributed Orders",
  [SemanticTable.ShippingClaims]: "Shipping Claims",
  [SemanticTable.DraftReturnPendingReturnItems]:
    "Draft Return Pending Return Items",
  [SemanticTable.Conversations]: "Conversations",
  [SemanticTable.ConversationMessages]: "Conversation Messages",
  [SemanticTable.ReturnTags]: "Return Tags",
  [SemanticTable.SatisfactionResponses]: "Satisfaction Responses",
  [SemanticTable.OrderExtendedWarranties]: "Order Extended Warranties",
  [SemanticTable.FailedDiscountRescueEvents]: "Failed Discount Rescue Events",
  [SemanticTable.CustomerTags]: "Customer Tags",
  [SemanticTable.CustomCustomerEvents]: "Custom Customer Events",
  [SemanticTable.CustomerCustomFields]: "Customer Custom Fields",
  [SemanticTable.ProductTags]: "Product Tags",
  [SemanticTable.FulfillmentGroupTrackingEvents]:
    "Fulfillment Group Tracking Events",
  [SemanticTable.ProductCollections]: "Product Collections",
  [SemanticTable.ProductReviews]: "Product Reviews",
  [SemanticTable.DailyShopperSessions]: "Daily Shopper Sessions",
  [SemanticTable.MarketingAgentDecisions]: "Marketing Agent Decisions",
  [SemanticTable.MarketingAgentDecisionTags]: "Marketing Agent Decision Tags",
  [SemanticTable.CustomerGroupMemberships]: "Customer Group Memberships",
  [SemanticTable.CampaignRecipients]: "Campaign Recipients",
  [SemanticTable.CheckoutDeliveryOptions]: "Checkout Delivery Options",
  [SemanticTable.CheckoutDeliveryGroups]: "Checkout Delivery Groups",
  [SemanticTable.CheckoutInfoChanges]: "Checkout Info Changes",
  [SemanticTable.CheckoutShippingStepEvents]: "Checkout Shipping Step Events",
  [SemanticTable.CheckoutOrderCreatedEvents]: "Checkout Order Created Events",
  [SemanticTable.CheckoutWidgetInteractions]: "Checkout Widget Interactions",
  [SemanticTable.PostPurchaseSplitAssignments]:
    "Post-Purchase Split Assignments",
  [SemanticTable.MarketingSubscriptionEvents]: "Marketing Subscription Events",
  [SemanticTable.BackInStockEvents]: "Back In Stock Events",
  [SemanticTable.CollectionViewEvents]: "Collection View Events",
  [SemanticTable.ProductViewEvents]: "Product View Events",
  [SemanticTable.StorefrontCheckoutEvents]: "Storefront Checkout Events",
  [SemanticTable.CartChangeEvents]: "Cart Change Events",
  [SemanticTable.TryFirstPlans]: "Try First Plans",
  [SemanticTable.TryFirstContracts]: "Try First Contracts",
  [SemanticTable.ReturnAssessmentResponses]: "Return Assessment Responses",
  [SemanticTable.ReturnAssessmentDefinitionFields]:
    "Return Assessment Definition Fields",
  [SemanticTable.ReturnAssessments]: "Return Assessments",
  [SemanticTable.TrackingPageEvents]: "Tracking Page Events",
  [SemanticTable.OrderTrackingPageOfferClicks]:
    "Order Tracking Page Offer Clicks",
  [SemanticTable.OrderTrackingEmailEvents]: "Order Tracking Email Events",
  [SemanticTable.OrderTrackingDetails]: "Order Tracking Details",
  [SemanticTable.InboundMessages]: "Inbound Messages",
  [SemanticTable.MercuryShippingLines]: "Shipping AI Shipping Lines",
  [SemanticTable.OrderPaymentGatewayNames]: "Order Payment Gateway Names",
  [SemanticTable.Integrations]: "Integrations",
  [SemanticTable.Customers]: "Customers",
  [SemanticTable.SupportTickets]: "Support Tickets",
  [SemanticTable.SupportTicketMessages]: "Support Ticket Messages",
  [SemanticTable.MessageTopicLinks]: "Message Topic Links",
  [SemanticTable.SupportTicketTopics]: "Support Ticket Topics",
});

export const SemanticTableDefaultAlias = defineSemanticTableMap({
  [SemanticTable.CheckoutWidgets]: "cw",
  [SemanticTable.CheckoutWidgetAttributedOrderLineItems]: "cwa",
  [SemanticTable.OrderLineItemProperties]: "olip",
  [SemanticTable.FulfillmentGroups]: "fg",
  [SemanticTable.FulfillmentGroupLineItems]: "fgli",
  [SemanticTable.FulfillmentGroupShipments]: "fgs",
  [SemanticTable.FulfillmentGroupShipmentSurcharges]: "fgss",
  [SemanticTable.FulfillmentGroupFulfillmentOrders]: "fgfo",
  [SemanticTable.Orders]: "ord",
  [SemanticTable.OrderLineItems]: "oli",
  [SemanticTable.ExchangeOrders]: "xord",
  [SemanticTable.ReturnLineItems]: "rli",
  [SemanticTable.OrderFulfillments]: "ofl",
  [SemanticTable.OrderFulfillmentLineItems]: "ofli",
  [SemanticTable.ReturnShipmentTrackingDetails]: "rstd",
  [SemanticTable.ReturnLineItemMultipleChoice]: "rlmc",
  [SemanticTable.Returns]: "ret",
  [SemanticTable.ReturnPaymentIntents]: "rpi",
  [SemanticTable.ReturnShipments]: "rsh",
  [SemanticTable.MarketingOutreachEvents]: "moe",
  [SemanticTable.Campaigns]: "cmp",
  [SemanticTable.Automations]: "aut",
  [SemanticTable.CheckoutVisits]: "cv",
  [SemanticTable.CheckoutVisitAdvanced]: "cva",
  [SemanticTable.CheckoutVisitTreatments]: "cvt",
  [SemanticTable.Treatments]: "trt",
  [SemanticTable.Splits]: "spl",
  [SemanticTable.OrderShippingLines]: "osl",
  [SemanticTable.OrderRefundLineItems]: "orl",
  [SemanticTable.PostPurchaseUpsellEvents]: "ppe",
  [SemanticTable.PostPurchaseUpsellPages]: "upg",
  [SemanticTable.Products]: "prd",
  [SemanticTable.InventoryLevels]: "ivl",
  [SemanticTable.StorageLocations]: "stl",
  [SemanticTable.Locations]: "loc",
  [SemanticTable.Bills]: "bil",
  [SemanticTable.BillingCharges]: "bch",
  [SemanticTable.RescuePanelMetrics]: "rpm",
  [SemanticTable.RescuePanelAttributedOrders]: "rpao",
  [SemanticTable.ShippingClaims]: "sc",
  [SemanticTable.DraftReturnPendingReturnItems]: "drpi",
  [SemanticTable.Conversations]: "conv",
  [SemanticTable.ConversationMessages]: "cmsg",
  [SemanticTable.ReturnTags]: "rt",
  [SemanticTable.SatisfactionResponses]: "csat",
  [SemanticTable.OrderExtendedWarranties]: "oew",
  [SemanticTable.FailedDiscountRescueEvents]: "fdr",
  [SemanticTable.CustomerTags]: "ctg",
  [SemanticTable.CustomCustomerEvents]: "cce",
  [SemanticTable.CustomerCustomFields]: "ccf",
  [SemanticTable.ProductTags]: "ptg",
  [SemanticTable.FulfillmentGroupTrackingEvents]: "fgte",
  [SemanticTable.ProductCollections]: "pcl",
  [SemanticTable.ProductReviews]: "rev",
  [SemanticTable.DailyShopperSessions]: "dss",
  [SemanticTable.MarketingAgentDecisions]: "mad",
  [SemanticTable.MarketingAgentDecisionTags]: "madt",
  [SemanticTable.CustomerGroupMemberships]: "cgm",
  [SemanticTable.CampaignRecipients]: "cpr",
  [SemanticTable.CheckoutDeliveryOptions]: "cdo",
  [SemanticTable.CheckoutDeliveryGroups]: "cdg",
  [SemanticTable.CheckoutInfoChanges]: "cic",
  [SemanticTable.CheckoutShippingStepEvents]: "csse",
  [SemanticTable.CheckoutOrderCreatedEvents]: "coce",
  [SemanticTable.CheckoutWidgetInteractions]: "cwi",
  [SemanticTable.PostPurchaseSplitAssignments]: "ppsa",
  [SemanticTable.MarketingSubscriptionEvents]: "mse",
  [SemanticTable.BackInStockEvents]: "bise",
  [SemanticTable.CollectionViewEvents]: "clve",
  [SemanticTable.ProductViewEvents]: "pve",
  [SemanticTable.StorefrontCheckoutEvents]: "sfce",
  [SemanticTable.CartChangeEvents]: "crce",
  [SemanticTable.TryFirstPlans]: "tfp",
  [SemanticTable.TryFirstContracts]: "tct",
  [SemanticTable.ReturnAssessmentResponses]: "rasr",
  [SemanticTable.ReturnAssessmentDefinitionFields]: "radf",
  [SemanticTable.ReturnAssessments]: "rasm",
  [SemanticTable.TrackingPageEvents]: "tpe",
  [SemanticTable.OrderTrackingPageOfferClicks]: "otoc",
  [SemanticTable.OrderTrackingEmailEvents]: "otee",
  [SemanticTable.OrderTrackingDetails]: "otd",
  [SemanticTable.InboundMessages]: "ibm",
  [SemanticTable.MercuryShippingLines]: "msl",
  [SemanticTable.OrderPaymentGatewayNames]: "opgn",
  [SemanticTable.Integrations]: "intg",
  [SemanticTable.Customers]: "cus",
  [SemanticTable.SupportTickets]: "stk",
  [SemanticTable.SupportTicketMessages]: "stm",
  [SemanticTable.MessageTopicLinks]: "mtl",
  [SemanticTable.SupportTicketTopics]: "stt",
});

export const SemanticTableScopedName = defineSemanticTableMap({
  [SemanticTable.CheckoutWidgets]: "scoped_cw",
  [SemanticTable.CheckoutWidgetAttributedOrderLineItems]: "scoped_cwa",
  [SemanticTable.OrderLineItemProperties]: "scoped_olip",
  [SemanticTable.FulfillmentGroups]: "scoped_fg",
  [SemanticTable.FulfillmentGroupLineItems]: "scoped_fgli",
  [SemanticTable.FulfillmentGroupShipments]: "scoped_fgs",
  [SemanticTable.FulfillmentGroupShipmentSurcharges]: "scoped_fgss",
  [SemanticTable.FulfillmentGroupFulfillmentOrders]: "scoped_fgfo",
  [SemanticTable.Orders]: "scoped_ord",
  [SemanticTable.OrderLineItems]: "scoped_oli",
  [SemanticTable.ExchangeOrders]: "scoped_xord",
  [SemanticTable.ReturnLineItems]: "scoped_rli",
  [SemanticTable.OrderFulfillments]: "scoped_ofl",
  [SemanticTable.OrderFulfillmentLineItems]: "scoped_ofli",
  [SemanticTable.ReturnShipmentTrackingDetails]: "scoped_rstd",
  [SemanticTable.ReturnLineItemMultipleChoice]: "scoped_rlmc",
  [SemanticTable.Returns]: "scoped_ret",
  [SemanticTable.ReturnPaymentIntents]: "scoped_rpi",
  [SemanticTable.ReturnShipments]: "scoped_rsh",
  [SemanticTable.MarketingOutreachEvents]: "scoped_moe",
  [SemanticTable.Campaigns]: "scoped_cmp",
  [SemanticTable.Automations]: "scoped_aut",
  [SemanticTable.CheckoutVisits]: "scoped_cv",
  [SemanticTable.CheckoutVisitAdvanced]: "scoped_cva",
  [SemanticTable.CheckoutVisitTreatments]: "scoped_cvt",
  [SemanticTable.Treatments]: "scoped_trt",
  [SemanticTable.Splits]: "scoped_spl",
  [SemanticTable.OrderShippingLines]: "scoped_osl",
  [SemanticTable.OrderRefundLineItems]: "scoped_orl",
  [SemanticTable.PostPurchaseUpsellEvents]: "scoped_ppe",
  [SemanticTable.PostPurchaseUpsellPages]: "scoped_upg",
  [SemanticTable.Products]: "scoped_prd",
  [SemanticTable.InventoryLevels]: "scoped_ivl",
  [SemanticTable.StorageLocations]: "scoped_stl",
  [SemanticTable.Locations]: "scoped_loc",
  [SemanticTable.Bills]: "scoped_bil",
  [SemanticTable.BillingCharges]: "scoped_bch",
  [SemanticTable.RescuePanelMetrics]: "scoped_rpm",
  [SemanticTable.RescuePanelAttributedOrders]: "scoped_rpao",
  [SemanticTable.ShippingClaims]: "scoped_sc",
  [SemanticTable.DraftReturnPendingReturnItems]: "scoped_drpi",
  [SemanticTable.Conversations]: "scoped_conv",
  [SemanticTable.ConversationMessages]: "scoped_cmsg",
  [SemanticTable.ReturnTags]: "scoped_rt",
  [SemanticTable.SatisfactionResponses]: "scoped_csat",
  [SemanticTable.OrderExtendedWarranties]: "scoped_oew",
  [SemanticTable.FailedDiscountRescueEvents]: "scoped_fdr",
  [SemanticTable.CustomerTags]: "scoped_ctg",
  [SemanticTable.CustomCustomerEvents]: "scoped_cce",
  [SemanticTable.CustomerCustomFields]: "scoped_ccf",
  [SemanticTable.ProductTags]: "scoped_ptg",
  [SemanticTable.FulfillmentGroupTrackingEvents]: "scoped_fgte",
  [SemanticTable.ProductCollections]: "scoped_pcl",
  [SemanticTable.ProductReviews]: "scoped_rev",
  [SemanticTable.DailyShopperSessions]: "scoped_dss",
  [SemanticTable.MarketingAgentDecisions]: "scoped_mad",
  [SemanticTable.MarketingAgentDecisionTags]: "scoped_madt",
  [SemanticTable.CustomerGroupMemberships]: "scoped_cgm",
  [SemanticTable.CampaignRecipients]: "scoped_cpr",
  [SemanticTable.CheckoutDeliveryOptions]: "scoped_cdo",
  [SemanticTable.CheckoutDeliveryGroups]: "scoped_cdg",
  [SemanticTable.CheckoutInfoChanges]: "scoped_cic",
  [SemanticTable.CheckoutShippingStepEvents]: "scoped_csse",
  [SemanticTable.CheckoutOrderCreatedEvents]: "scoped_coce",
  [SemanticTable.CheckoutWidgetInteractions]: "scoped_cwi",
  [SemanticTable.PostPurchaseSplitAssignments]: "scoped_ppsa",
  [SemanticTable.MarketingSubscriptionEvents]: "scoped_mse",
  [SemanticTable.BackInStockEvents]: "scoped_bise",
  [SemanticTable.CollectionViewEvents]: "scoped_clve",
  [SemanticTable.ProductViewEvents]: "scoped_pve",
  [SemanticTable.StorefrontCheckoutEvents]: "scoped_sfce",
  [SemanticTable.CartChangeEvents]: "scoped_crce",
  [SemanticTable.TryFirstPlans]: "scoped_tfp",
  [SemanticTable.TryFirstContracts]: "scoped_tct",
  [SemanticTable.ReturnAssessmentResponses]: "scoped_rasr",
  [SemanticTable.ReturnAssessmentDefinitionFields]: "scoped_radf",
  [SemanticTable.ReturnAssessments]: "scoped_rasm",
  [SemanticTable.TrackingPageEvents]: "scoped_tpe",
  [SemanticTable.OrderTrackingPageOfferClicks]: "scoped_otoc",
  [SemanticTable.OrderTrackingEmailEvents]: "scoped_otee",
  [SemanticTable.OrderTrackingDetails]: "scoped_otd",
  [SemanticTable.InboundMessages]: "scoped_ibm",
  [SemanticTable.MercuryShippingLines]: "scoped_msl",
  [SemanticTable.OrderPaymentGatewayNames]: "scoped_opgn",
  [SemanticTable.Integrations]: "scoped_intg",
  [SemanticTable.Customers]: "scoped_cus",
  [SemanticTable.SupportTickets]: "scoped_stk",
  [SemanticTable.SupportTicketMessages]: "scoped_stm",
  [SemanticTable.MessageTopicLinks]: "scoped_mtl",
  [SemanticTable.SupportTicketTopics]: "scoped_stt",
});

export const CustomAnalyticsPhysicalTable = defineSemanticTableMap({
  [SemanticTable.CheckoutWidgets]: "STAGING.COCKROACHDB.STG_CHECKOUT_WIDGETS",
  [SemanticTable.CheckoutWidgetAttributedOrderLineItems]:
    "REDO.JOIN_TABLES.CHECKOUT_WIDGET_ATTRIBUTED_ORDER_LINE_ITEMS",
  [SemanticTable.OrderLineItemProperties]:
    "STAGING.MONGODB.STG_ORDER_LINE_ITEM_PROPERTIES",
  [SemanticTable.FulfillmentGroups]:
    "STAGING.COCKROACHDB.STG_FULFILLMENT_GROUPS",
  [SemanticTable.FulfillmentGroupLineItems]:
    "STAGING.COCKROACHDB.STG_FULFILLMENT_GROUP_LINE_ITEMS",
  [SemanticTable.FulfillmentGroupShipments]:
    "STAGING.COCKROACHDB.STG_FULFILLMENT_GROUP_SHIPMENTS",
  [SemanticTable.FulfillmentGroupShipmentSurcharges]:
    "STAGING.COCKROACHDB.STG_FULFILLMENT_GROUP_SHIPMENT_SURCHARGES",
  [SemanticTable.FulfillmentGroupFulfillmentOrders]:
    "STAGING.COCKROACHDB.STG_FULFILLMENT_GROUP_FULFILLMENT_ORDERS",
  [SemanticTable.Orders]: "STAGING.MONGODB.STG_ORDERS",
  [SemanticTable.OrderLineItems]: "STAGING.MONGODB.STG_ORDER_LINE_ITEMS",
  [SemanticTable.ExchangeOrders]: "STAGING.MONGODB.STG_RETURN_EXCHANGE_ORDERS",
  [SemanticTable.ReturnLineItems]: "STAGING.MONGODB.STG_RETURN_LINE_ITEMS",
  [SemanticTable.OrderFulfillments]: "STAGING.MONGODB.STG_ORDER_FULFILLMENTS",
  [SemanticTable.OrderFulfillmentLineItems]:
    "STAGING.MONGODB.STG_ORDER_FULFILLMENT_LINE_ITEMS",
  [SemanticTable.ReturnShipmentTrackingDetails]:
    "STAGING.MONGODB.STG_RETURN_SHIPMENT_TRACKING_DETAILS",
  [SemanticTable.ReturnLineItemMultipleChoice]:
    "STAGING.MONGODB.STG_RETURNS_MULTIPLE_CHOICE",
  [SemanticTable.Returns]: "STAGING.MONGODB.STG_RETURNS",
  [SemanticTable.ReturnPaymentIntents]:
    "STAGING.MONGODB.STG_RETURN_PAYMENT_INTENTS",
  [SemanticTable.ReturnShipments]: "STAGING.MONGODB.STG_RETURN_SHIPMENTS",
  [SemanticTable.MarketingOutreachEvents]:
    "STAGING.MONGODB.STG_CUSTOMER_EVENTS_OUTREACH",
  [SemanticTable.Campaigns]: "STAGING.COCKROACHDB.STG_CAMPAIGNS",
  [SemanticTable.Automations]: "STAGING.COCKROACHDB.STG_ADVANCED_FLOWS",
  [SemanticTable.CheckoutVisits]:
    "REDO.JOIN_TABLES.CHECKOUT_OPTIMIZATION_CHECKOUT_VISITS",
  [SemanticTable.CheckoutVisitAdvanced]:
    "REDO.JOIN_TABLES.CHECKOUT_OPTIMIZATION_CHECKOUT_VISITS_ADVANCED",
  [SemanticTable.CheckoutVisitTreatments]:
    "REDO.JOIN_TABLES.CHECKOUT_VISIT_TREATMENTS",
  [SemanticTable.Treatments]: "REDO.JOIN_TABLES.TREATMENTS_CONSOLIDATED",
  [SemanticTable.Splits]: "STAGING.COCKROACHDB.STG_SPLITS",
  [SemanticTable.OrderShippingLines]:
    "STAGING.MONGODB.STG_ORDER_SHIPPING_LINES",
  [SemanticTable.OrderRefundLineItems]:
    "STAGING.MONGODB.STG_ORDER_REFUND_LINE_ITEMS",
  [SemanticTable.PostPurchaseUpsellEvents]:
    "STAGING.MONGODB.STG_CUSTOMER_EVENTS_POST_PURCHASE",
  [SemanticTable.PostPurchaseUpsellPages]:
    "STAGING.COCKROACHDB.STG_UPSELL_PAGES",
  [SemanticTable.Products]: "STAGING.COCKROACHDB.STG_CRDB_PRODUCTS",
  [SemanticTable.InventoryLevels]:
    "STAGING.COCKROACHDB.STG_CRDB_INVENTORY_LEVEL",
  [SemanticTable.StorageLocations]:
    "STAGING.COCKROACHDB.STG_CRDB_STORAGE_LOCATION",
  [SemanticTable.Locations]: "STAGING.COCKROACHDB.STG_CRDB_LOCATION",
  [SemanticTable.Bills]: "STAGING.COCKROACHDB.STG_CRDB_BILL",
  [SemanticTable.BillingCharges]: "STAGING.COCKROACHDB.STG_CRDB_BILLING_CHARGE",
  [SemanticTable.RescuePanelMetrics]:
    "REDO.CHECKOUT_OPTIMIZATION.RESCUE_PANEL_METRICS",
  [SemanticTable.RescuePanelAttributedOrders]:
    "REDO.JOIN_TABLES.RESCUE_PANEL_ATTRIBUTED_ORDERS",
  [SemanticTable.ShippingClaims]: "STAGING.COCKROACHDB.STG_SHIPPING_CLAIMS",
  [SemanticTable.DraftReturnPendingReturnItems]:
    "STAGING.COCKROACHDB.STG_DRAFT_RETURN_PENDING_RETURN_ITEMS",
  [SemanticTable.Conversations]: "STAGING.MONGODB.STG_CONVERSATIONS",
  [SemanticTable.ConversationMessages]:
    "STAGING.MONGODB.STG_CONVERSATION_MESSAGES",
  [SemanticTable.ReturnTags]: "STAGING.MONGODB.STG_RETURN_TAGS",
  [SemanticTable.SatisfactionResponses]:
    "STAGING.COCKROACHDB.STG_SATISFACTION_RESPONSES",
  [SemanticTable.OrderExtendedWarranties]:
    "STAGING.MONGODB.STG_ORDER_EXTENDED_WARRANTIES",
  [SemanticTable.FailedDiscountRescueEvents]:
    "STAGING.MONGODB.STG_CUSTOMER_EVENTS_FAILED_DISCOUNT_RESCUE",
  [SemanticTable.CustomerTags]: "STAGING.MONGODB.STG_CUSTOMER_TAGS",
  [SemanticTable.CustomCustomerEvents]:
    "STAGING.MONGODB.STG_CUSTOM_CUSTOMER_EVENTS",
  [SemanticTable.CustomerCustomFields]:
    "STAGING.MONGODB.STG_CUSTOMER_CUSTOM_FIELDS",
  [SemanticTable.ProductTags]: "STAGING.MONGODB.STG_PRODUCT_TAGS",
  [SemanticTable.FulfillmentGroupTrackingEvents]:
    "STAGING.COCKROACHDB.STG_FULFILLMENT_GROUP_TRACKING_EVENTS",
  [SemanticTable.ProductCollections]: "STAGING.MONGODB.STG_PRODUCT_COLLECTIONS",
  [SemanticTable.ProductReviews]: "STAGING.COCKROACHDB.STG_REVIEWS",
  [SemanticTable.DailyShopperSessions]:
    "STAGING.COCKROACHDB.STG_DAILY_USER_SESSION_SUMMARIES",
  [SemanticTable.MarketingAgentDecisions]:
    "STAGING.COCKROACHDB.STG_MARKETING_AGENT_DECISIONS",
  [SemanticTable.MarketingAgentDecisionTags]:
    "STAGING.COCKROACHDB.STG_MARKETING_AGENT_DECISION_TAGS",
  [SemanticTable.CustomerGroupMemberships]:
    "STAGING.MONGODB.STG_CUSTOMER_GROUP_MEMBERSHIPS",
  [SemanticTable.CampaignRecipients]: "STAGING.MONGODB.STG_CAMPAIGN_RECIPIENTS",
  [SemanticTable.CheckoutDeliveryOptions]:
    "STAGING.MONGODB.STG_CUSTOMER_EVENT_DELIVERY_OPTIONS",
  [SemanticTable.CheckoutDeliveryGroups]:
    "STAGING.MONGODB.STG_CUSTOMER_EVENT_DELIVERY_GROUPS",
  [SemanticTable.CheckoutInfoChanges]:
    "STAGING.MONGODB.STG_CUSTOMER_EVENTS_CHECKOUT_INFO_CHANGED",
  [SemanticTable.CheckoutShippingStepEvents]:
    "STAGING.MONGODB.STG_CUSTOMER_EVENTS_CHECKOUT_RATES_SHOWN",
  [SemanticTable.CheckoutOrderCreatedEvents]:
    "STAGING.MONGODB.STG_CUSTOMER_EVENTS_ORDER_CREATED",
  [SemanticTable.CheckoutWidgetInteractions]:
    "STAGING.MONGODB.STG_ORDER_CHECKOUT_OPTIMIZATION_ANALYTICS",
  [SemanticTable.PostPurchaseSplitAssignments]:
    "STAGING.MONGODB.STG_CUSTOMER_EVENT_PPU_SPLITS",
  [SemanticTable.MarketingSubscriptionEvents]:
    "STAGING.MONGODB.STG_CUSTOMER_EVENTS",
  [SemanticTable.BackInStockEvents]: "STAGING.MONGODB.STG_CUSTOMER_EVENTS",
  [SemanticTable.CollectionViewEvents]: "STAGING.MONGODB.STG_CUSTOMER_EVENTS",
  [SemanticTable.ProductViewEvents]: "STAGING.MONGODB.STG_CUSTOMER_EVENTS",
  [SemanticTable.StorefrontCheckoutEvents]:
    "STAGING.MONGODB.STG_CUSTOMER_EVENTS",
  [SemanticTable.CartChangeEvents]: "STAGING.MONGODB.STG_CUSTOMER_EVENTS",
  [SemanticTable.TryFirstPlans]:
    "STAGING.COCKROACHDB.STG_TNPL_CONTRACT_TEMPLATES",
  [SemanticTable.TryFirstContracts]: "STAGING.COCKROACHDB.STG_TNPL_CONTRACTS",
  [SemanticTable.ReturnAssessmentResponses]:
    "STAGING.MONGODB.STG_RETURN_LINE_ITEMS_ASSESSMENT_RESPONSES",
  [SemanticTable.ReturnAssessmentDefinitionFields]:
    "STAGING.COCKROACHDB.STG_RETURN_ASSESSMENT_DEFINITION_FIELDS",
  [SemanticTable.ReturnAssessments]:
    "STAGING.COCKROACHDB.STG_RETURN_ASSESSMENTS",
  [SemanticTable.TrackingPageEvents]:
    "STAGING.MONGODB.STG_CUSTOMER_EVENTS_TRACKING_PAGE",
  [SemanticTable.OrderTrackingPageOfferClicks]:
    "STAGING.MONGODB.STG_ORDER_TRACKING_ANALYTICS_PAGES",
  [SemanticTable.OrderTrackingEmailEvents]:
    "STAGING.MONGODB.STG_ORDER_TRACKING_ANALYTICS_EMAILS",
  [SemanticTable.OrderTrackingDetails]:
    "STAGING.MONGODB.STG_ORDER_TRACKING_DETAILS",
  [SemanticTable.InboundMessages]:
    "STAGING.MONGODB.STG_CUSTOMER_EVENTS_INBOUND_RECEIVED",
  [SemanticTable.MercuryShippingLines]:
    "STAGING.MONGODB.STG_ORDER_MERCURY_SHIPPING_LINES",
  [SemanticTable.OrderPaymentGatewayNames]:
    "STAGING.MONGODB.STG_ORDER_PAYMENT_GATEWAY_NAMES",
  [SemanticTable.Integrations]: "STAGING.MONGODB.STG_INTEGRATIONS",
  [SemanticTable.Customers]: "STAGING.MONGODB.STG_CUSTOMERS",
  [SemanticTable.SupportTickets]: "STAGING.COCKROACHDB.STG_CRDB_SUPPORT_TICKET",
  [SemanticTable.SupportTicketMessages]: "STAGING.COCKROACHDB.STG_CRDB_MESSAGE",
  [SemanticTable.MessageTopicLinks]:
    "STAGING.COCKROACHDB.STG_CRDB_MESSAGE_TOPIC_LINK",
  [SemanticTable.SupportTicketTopics]: "STAGING.COCKROACHDB.STG_CRDB_TOPIC",
});

export type CustomAnalyticsPhysicalTableName =
  (typeof CustomAnalyticsPhysicalTable)[SemanticTableName];

function fieldValues<const TFields extends Record<string, string>>(
  fields: TFields,
): readonly [TFields[keyof TFields], ...TFields[keyof TFields][]] {
  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
  return Object.values(fields) as [
    TFields[keyof TFields],
    ...TFields[keyof TFields][],
  ];
}

export const CheckoutWidgetField = {
  CreatedAt: "created_at",
  Name: "name",
  TeamId: "team_id",
  Type: "type",
  WidgetId: "widget_id",
} as const;

export type CheckoutWidgetField =
  (typeof CheckoutWidgetField)[keyof typeof CheckoutWidgetField];

export const CHECKOUT_WIDGET_FIELDS = fieldValues(CheckoutWidgetField);

export const CheckoutWidgetPhysicalField = {
  CreatedAt: "created_at",
  Name: "name",
  TeamId: "team_id",
  Type: "type",
  WidgetId: "widget_id",
} as const;

export type CheckoutWidgetPhysicalField =
  (typeof CheckoutWidgetPhysicalField)[keyof typeof CheckoutWidgetPhysicalField];

export const CHECKOUT_WIDGET_PHYSICAL_FIELDS = fieldValues(
  CheckoutWidgetPhysicalField,
);

export const CheckoutWidgetAttributedOrderLineItemsField = {
  AttributedQuantity: "attributed_quantity",
  OrderId: "order_id",
  OrderLineItemId: "order_line_item_id",
  TeamId: "team_id",
  WidgetId: "widget_id",
} as const;

export type CheckoutWidgetAttributedOrderLineItemsField =
  (typeof CheckoutWidgetAttributedOrderLineItemsField)[keyof typeof CheckoutWidgetAttributedOrderLineItemsField];

export const CHECKOUT_WIDGET_ATTRIBUTED_ORDER_LINE_ITEMS_FIELDS = fieldValues(
  CheckoutWidgetAttributedOrderLineItemsField,
);

export const CheckoutWidgetAttributedOrderLineItemsPhysicalField = {
  AttributedQuantity: "attributed_quantity",
  OrderId: "order_id",
  OrderLineItemId: "order_line_item_id",
  TeamId: "team_id",
  WidgetId: "widget_id",
} as const;

export type CheckoutWidgetAttributedOrderLineItemsPhysicalField =
  (typeof CheckoutWidgetAttributedOrderLineItemsPhysicalField)[keyof typeof CheckoutWidgetAttributedOrderLineItemsPhysicalField];

export const CHECKOUT_WIDGET_ATTRIBUTED_ORDER_LINE_ITEMS_PHYSICAL_FIELDS =
  fieldValues(CheckoutWidgetAttributedOrderLineItemsPhysicalField);

export const OrderLineItemPropertyField = {
  LineItemId: "line_item_id",
  OrderId: "order_id",
  PropertyName: "property_name",
  PropertyValue: "property_value",
  TeamId: "team_id",
} as const;

export type OrderLineItemPropertyField =
  (typeof OrderLineItemPropertyField)[keyof typeof OrderLineItemPropertyField];

export const ORDER_LINE_ITEM_PROPERTY_FIELDS = fieldValues(
  OrderLineItemPropertyField,
);

export const OrderLineItemPropertyPhysicalField = {
  LineItemId: "line_item_id",
  OrderId: "order_id",
  PropertyName: "property_name",
  PropertyValue: "property_value",
  TeamId: "team_id",
} as const;

export type OrderLineItemPropertyPhysicalField =
  (typeof OrderLineItemPropertyPhysicalField)[keyof typeof OrderLineItemPropertyPhysicalField];

export const ORDER_LINE_ITEM_PROPERTY_PHYSICAL_FIELDS = fieldValues(
  OrderLineItemPropertyPhysicalField,
);

export const FulfillmentGroupField = {
  AssignedFulfillmentService: "assigned_fulfillment_service",
  CreatedAt: "created_at",
  CreatedFromSplit: "created_from_split",
  DeliveryMethod: "delivery_method",
  HasShipment: "has_shipment",
  Id: "_id",
  LabelDate: "label_date",
  LocationId: "location_id",
  LabelPurchasedAt: "label_purchased_at",
  OrderCreatedAt: "order_created_at",
  PackingSlipPrintedAt: "packing_slip_printed_at",
  ScanVerifiedAt: "scan_verified_at",
  SlaFulfillmentDate: "sla_fulfillment_date",
  Status: "status",
  Team: "team",
  TotalCustomerShippingPaid: "total_customer_shipping_paid",
  TotalItemQuantity: "total_item_quantity",
  TotalPrice: "total_price",
  UpdatedAt: "updated_at",
} as const;

export type FulfillmentGroupField =
  (typeof FulfillmentGroupField)[keyof typeof FulfillmentGroupField];

export const FULFILLMENT_GROUP_FIELDS = fieldValues(FulfillmentGroupField);

export const FulfillmentGroupPhysicalField = {
  AssignedFulfillmentService: "assigned_fulfillment_service",
  CreatedAt: "createdat",
  CreatedFromSplit: "created_from_split",
  DeliveryMethod: "delivery_method",
  HasShipment: "has_shipment",
  Id: "mongo_id",
  LabelDate: "label_date",
  LocationId: "location_id",
  LabelPurchasedAt: "label_purchased_at",
  OrderCreatedAt: "order_created_at",
  PackingSlipPrintedAt: "packing_slip_printed_at",
  ScanVerifiedAt: "scan_verified_at",
  SlaFulfillmentDate: "sla_fulfillment_date",
  Status: "status",
  Team: "team",
  TotalCustomerShippingPaid: "total_customer_shipping_paid",
  TotalItemQuantity: "total_item_quantity",
  TotalPrice: "total_price",
  UpdatedAt: "updatedat",
} as const;

export type FulfillmentGroupPhysicalField =
  (typeof FulfillmentGroupPhysicalField)[keyof typeof FulfillmentGroupPhysicalField];

export const FULFILLMENT_GROUP_PHYSICAL_FIELDS = fieldValues(
  FulfillmentGroupPhysicalField,
);

export const FulfillmentGroupLineItemField = {
  AssignedFulfillmentService: "assigned_fulfillment_service",
  FulfillmentGroupId: "fulfillment_group_id",
  FulfillmentOrderIndex: "fulfillment_order_index",
  LineItemIndex: "line_item_index",
  OrderCreatedAt: "order_created_at",
  OrderId: "order_id",
  Provider: "provider",
  Quantity: "quantity",
  RedoProductId: "redo_product_id",
  RequiresShipping: "requires_shipping",
  Sku: "sku",
  TeamId: "team_id",
  UnitPrice: "unit_price",
} as const;

export type FulfillmentGroupLineItemField =
  (typeof FulfillmentGroupLineItemField)[keyof typeof FulfillmentGroupLineItemField];

export const FULFILLMENT_GROUP_LINE_ITEM_FIELDS = fieldValues(
  FulfillmentGroupLineItemField,
);

export const FulfillmentGroupLineItemPhysicalField = {
  AssignedFulfillmentService: "assigned_fulfillment_service",
  FulfillmentGroupId: "mongo_id",
  FulfillmentOrderIndex: "fulfillment_order_index",
  LineItemIndex: "line_item_index",
  OrderCreatedAt: "order_created_at",
  OrderId: "order_id",
  Provider: "provider",
  Quantity: "quantity",
  RedoProductId: "redo_product_id",
  RequiresShipping: "requires_shipping",
  Sku: "sku",
  TeamId: "team",
  UnitPrice: "unit_price",
} as const;

export type FulfillmentGroupLineItemPhysicalField =
  (typeof FulfillmentGroupLineItemPhysicalField)[keyof typeof FulfillmentGroupLineItemPhysicalField];

export const FULFILLMENT_GROUP_LINE_ITEM_PHYSICAL_FIELDS = fieldValues(
  FulfillmentGroupLineItemPhysicalField,
);

export const FulfillmentGroupShipmentField = {
  AllShipmentsIndex: "all_shipments_index",
  AssignedFulfillmentService: "assigned_fulfillment_service",
  Carrier: "carrier",
  CarrierAccountId: "carrier_account_id",
  CreatedAt: "created_at",
  FulfillmentGroupId: "fulfillment_group_id",
  HeightInInches: "height_in_inches",
  IsMerchantCarrierAccount: "is_merchant_carrier_account",
  LengthInInches: "length_in_inches",
  ParcelName: "parcel_name",
  ParcelSubtype: "parcel_subtype",
  ParcelType: "parcel_type",
  SelectedRate: "selected_rate",
  Service: "service",
  ShipmentId: "shipment_id",
  TeamId: "team_id",
  TrackingCode: "tracking_code",
  TrackingStatus: "tracking_status",
  WeightInOz: "weight_in_oz",
  WidthInInches: "width_in_inches",
} as const;

export type FulfillmentGroupShipmentField =
  (typeof FulfillmentGroupShipmentField)[keyof typeof FulfillmentGroupShipmentField];

export const FULFILLMENT_GROUP_SHIPMENT_FIELDS = fieldValues(
  FulfillmentGroupShipmentField,
);

export const FulfillmentGroupShipmentPhysicalField = {
  AllShipmentsIndex: "all_shipments_index",
  AssignedFulfillmentService: "assigned_fulfillment_service",
  Carrier: "selected_carrier",
  CarrierAccountId: "carrier_account_id",
  CreatedAt: "shipment_created_at",
  FulfillmentGroupId: "mongo_id",
  HeightInInches: "height_in_inches",
  IsMerchantCarrierAccount: "is_merchant_carrier_account",
  LengthInInches: "length_in_inches",
  ParcelName: "parcel_name",
  ParcelSubtype: "parcel_subtype",
  ParcelType: "parcel_type",
  SelectedRate: "selected_rate",
  Service: "selected_service",
  ShipmentId: "shipment_id",
  TeamId: "team_id",
  TrackingCode: "tracking_code",
  TrackingStatus: "label_status",
  WeightInOz: "weight_in_oz",
  WidthInInches: "width_in_inches",
} as const;

export type FulfillmentGroupShipmentPhysicalField =
  (typeof FulfillmentGroupShipmentPhysicalField)[keyof typeof FulfillmentGroupShipmentPhysicalField];

export const FULFILLMENT_GROUP_SHIPMENT_PHYSICAL_FIELDS = fieldValues(
  FulfillmentGroupShipmentPhysicalField,
);

export const OrderField = {
  CheckoutToken: "checkout_token",
  CreatedAt: "created_at",
  CurrentSubtotalPrice: "current_subtotal_price",
  CurrentTotalPrice: "current_total_price",
  CustomerEmail: "customer_email",
  FinancialStatus: "financial_status",
  IsExchangeOrder: "is_exchange_order",
  IsPackageProtected: "is_package_protected",
  IsProtected: "is_protected",
  IsRedoEligible: "is_redo_eligible",
  OrderId: "order_id",
  OrderName: "order_name",
  OrderNumber: "order_number",
  OrderPresentmentCurrency: "order_presentment_currency",
  OrderTags: "order_tags",
  Provider: "provider",
  ProviderCancelledAt: "provider_cancelled_at",
  ProviderCreatedAt: "provider_created_at",
  ProviderOrderId: "provider_order_id",
  ProviderProcessedAt: "provider_processed_at",
  ShippingCountryCode: "shipping_country_code",
  ShippingProvince: "shipping_province",
  ShopifyCustomerId: "shopify_customer_id",
  SourceName: "source_name",
  SubtotalPrice: "subtotal_price",
  TeamId: "team_id",
  TotalPrice: "total_price",
  UpdatedAt: "updated_at",
} as const;

export type OrderField = (typeof OrderField)[keyof typeof OrderField];

export const ORDER_FIELDS = fieldValues(OrderField);

export const OrderPhysicalField = {
  CheckoutToken: "checkout_token",
  CreatedAt: "created_at",
  CurrentSubtotalPrice: "current_subtotal_price",
  CurrentTotalPrice: "current_total_price",
  Email: "email",
  FinancialStatus: "financial_status",
  IsExchangeOrder: "is_exchange_order",
  IsPackageProtected: "is_package_protected",
  IsProtected: "is_protected",
  IsRedoEligible: "is_redo_eligible",
  OrderId: "order_id",
  OrderName: "order_name",
  OrderNumber: "order_number",
  OrderPresentmentCurrency: "total_price_set_presentment_currency_code",
  OrderTags: "order_tags_array",
  Provider: "provider",
  ShippingCountryCode: "shipping_country_code",
  ShippingProvince: "shipping_province",
  ShopifyCancelledAt: "shopify_cancelled_at",
  ShopifyCreatedAt: "shopify_created_at",
  ShopifyCustomerId: "customer_id",
  ShopifyOrderId: "shopify_order_id",
  ShopifyProcessedAt: "shopify_processed_at",
  SourceName: "source_name",
  SubtotalPrice: "subtotal_price",
  TeamId: "team_id",
  TotalPrice: "total_price",
  UpdatedAt: "updated_at",
} as const;

export type OrderPhysicalField =
  (typeof OrderPhysicalField)[keyof typeof OrderPhysicalField];

export const ORDER_PHYSICAL_FIELDS = fieldValues(OrderPhysicalField);

export const OrderLineItemField = {
  CurrentQuantity: "current_quantity",
  DiscountAllocations: "discount_allocations",
  IsFinalSaleProtected: "is_final_sale_protected",
  IsGiftCard: "is_gift_card",
  IsProtected: "is_protected",
  IsTaxable: "is_taxable",
  LineItemId: "line_item_id",
  OrderId: "order_id",
  PreTaxPrice: "pre_tax_price",
  Price: "price",
  ProductId: "product_id",
  ProductTitle: "product_title",
  Quantity: "quantity",
  RequiresShipping: "requires_shipping",
  Sku: "sku",
  Tags: "tags",
  TaxPrice: "tax_price",
  TeamId: "team_id",
  UnitPrice: "unit_price",
  VariantId: "variant_id",
  VariantTitle: "variant_title",
  Vendor: "vendor",
} as const;

export type OrderLineItemField =
  (typeof OrderLineItemField)[keyof typeof OrderLineItemField];

export const ORDER_LINE_ITEM_FIELDS = fieldValues(OrderLineItemField);

export const OrderLineItemPhysicalField = {
  CurrentQuantity: "current_quantity",
  DiscountAllocations: "discount_allocations",
  IsFinalSaleProtected: "is_final_sale_protected",
  IsGiftCard: "is_gift_card",
  IsProtected: "is_protected",
  IsTaxable: "is_taxable",
  LineItemId: "line_item_id",
  OrderId: "order_id",
  PreTaxPrice: "pre_tax_price",
  Price: "price",
  ProductId: "product_id",
  ProductTitle: "product_title",
  Quantity: "quantity",
  RequiresShipping: "requires_shipping",
  Sku: "sku",
  Tags: "tags_array",
  TaxPrice: "tax_price",
  TeamId: "team_id",
  UnitPrice: "unit_price",
  VariantId: "variant_id",
  VariantTitle: "variant_title",
  Vendor: "vendor",
} as const;

export type OrderLineItemPhysicalField =
  (typeof OrderLineItemPhysicalField)[keyof typeof OrderLineItemPhysicalField];

export const ORDER_LINE_ITEM_PHYSICAL_FIELDS = fieldValues(
  OrderLineItemPhysicalField,
);

export const ExchangeOrderField = {
  ExchangeOrderIndex: "exchange_order_index",
  OrderName: "order_name",
  ProviderOrderId: "provider_order_id",
  ReturnId: "return_id",
  Source: "source",
  TeamId: "team_id",
} as const;

export type ExchangeOrderField =
  (typeof ExchangeOrderField)[keyof typeof ExchangeOrderField];

export const EXCHANGE_ORDER_FIELDS = fieldValues(ExchangeOrderField);

export const ExchangeOrderPhysicalField = {
  ExchangeOrderIndex: "exchange_order_index",
  OrderName: "order_name",
  ProviderOrderId: "provider_order_id",
  ReturnId: "return_id",
  Source: "source",
  TeamId: "team_id",
} as const;

export type ExchangeOrderPhysicalField =
  (typeof ExchangeOrderPhysicalField)[keyof typeof ExchangeOrderPhysicalField];

export const EXCHANGE_ORDER_PHYSICAL_FIELDS = fieldValues(
  ExchangeOrderPhysicalField,
);

export const ReturnField = {
  AdvancedExchangeItems: "advanced_exchange_items",
  AdjustmentValue: "adjustment_value",
  PriceAdjustmentV2: "price_adjustment_v2",
  AdjustmentValueV1: "adjustment_value_v1",
  ApprovedAt: "approved_at",
  ApprovedBy: "approved_by",
  CompletedAt: "completed_at",
  CreatedBy: "created_by",
  IsCoveredByRedo: "is_covered_by_redo",
  LabelPayer: "label_payer",
  PickupPayer: "pickup_payer",
  ProcessedBy: "processed_by",
  ExchangeOrder: "exchange_order",
  OrderName: "order_name",
  CreatedAt: "created_at",
  ExchangeValue: "exchange_value",
  ExternalId: "external_id",
  RefundValue: "refund_value",
  StoreCreditValue: "store_credit_value",
  TaxValue: "tax_value",
  TotalAdjustments: "total_adjustments",
  TotalBonuses: "total_bonuses",
  ShippingFee: "shipping_fee",
  TotalFees: "total_fees",
  ValueBeforeTaxAndAdjustments: "value_before_tax_and_adjustments",
  Currency: "currency",
  Damaged: "damaged",
  Id: "_id",
  IdempotencyKey: "idempotency_key",
  ReturnDestinationAddressLine1: "return_destination_address_line_1",
  ReturnDestinationAddressLine2: "return_destination_address_line_2",
  ReturnDestinationCity: "return_destination_city",
  ReturnDestinationCountry: "return_destination_country",
  ReturnDestinationCountryCode: "return_destination_country_code",
  ReturnDestinationName: "return_destination_name",
  ReturnDestinationProvince: "return_destination_province",
  ReturnDestinationZip: "return_destination_zip",
  ReturnsCenterId: "returns_center_id",
  IsExchange: "is_exchange",
  IsRefund: "is_refund",
  IsStoreCredit: "is_store_credit",
  IsBlueYonderReturn: "is_blue_yonder_return",
  ProcessedAt: "processed_at",
  Provider: "provider",
  RejectType: "reject_type",
  ReturnBearSelectedReturnMethod: "return_bear_selected_return_method",
  ReturnMethod: "return_method",
  ReturnType: "type",
  Status: "status",
  Team: "team",
  TotalsCharge: "totals_charge",
  TotalsRefund: "totals_refund",
  TotalsStoreCredit: "totals_store_credit",
  UpdatedAt: "updated_at",
  UpsellAmount: "upsell_amount",
  WrongProduct: "wrong_product",
} as const;

export type ReturnField = (typeof ReturnField)[keyof typeof ReturnField];

export const RETURN_FIELDS = fieldValues(ReturnField);

export const ReturnPhysicalField = {
  AdvancedExchangeItems: "advancedexchangeitems",
  AdjustmentValue: "analytics_totals_return_adjustment",
  PriceAdjustmentV2: "atv2_return_price_adjustment",
  AdjustmentValueV1: "analytics_totals_v1_return_adjustment",
  ApprovedAt: "approvedAt",
  ApprovedBy: "approvedBy",
  CompletedAt: "completedat",
  CreatedBy: "createdBy",
  IsCoveredByRedo: "is_return_covered_by_redo",
  LabelPayer: "label_payer",
  PickupPayer: "pickup_payer",
  ProcessedBy: "processedBy",
  ExchangeOrder: "exchangeorder",
  OrderName: "atv2_order_name",
  CreatedAt: "createdat",
  ExchangeValue: "analytics_totals_return_exchange_value",
  ExternalId: "external_id",
  RefundValue: "analytics_totals_return_refund_value",
  StoreCreditValue: "analytics_totals_return_store_credit_value",
  TaxValue: "analytics_totals_return_tax",
  TotalAdjustments: "analytics_totals_return_adjustment",
  TotalBonuses: "atv2_return_total_bonuses",
  ShippingFee: "atv2_return_shipping_fee",
  TotalFees: "atv2_return_total_fees",
  ValueBeforeTaxAndAdjustments:
    "analytics_totals_return_value_no_tax_no_adjustment",
  Currency: "currency",
  Damaged: "damaged",
  Id: "_id",
  IdempotencyKey: "idempotency_key",
  ReturnDestinationAddressLine1: "merchant_address_address1",
  ReturnDestinationAddressLine2: "merchant_address_address2",
  ReturnDestinationCity: "merchant_address_city",
  ReturnDestinationCountry: "merchant_address_country",
  ReturnDestinationCountryCode: "merchant_address_country_code",
  ReturnDestinationName: "merchant_address_name",
  ReturnDestinationProvince: "merchant_address_province",
  ReturnDestinationZip: "merchant_address_zip",
  ReturnsCenterId: "returns_center_id",
  IsExchange: "is_exchange",
  IsRefund: "is_refund",
  IsStoreCredit: "is_store_credit",
  IsBlueYonderReturn: "is_blue_yonder_return",
  ProcessedAt: "processedAt",
  Provider: "provider",
  RejectType: "rejecttype",
  ReturnBearSelectedReturnMethod: "return_bear_selected_return_method",
  ReturnMethod: "return_method_type",
  ReturnType: "type",
  Status: "status",
  Team: "team",
  TotalsCharge: "totals_charge",
  TotalsRefund: "totals_refund",
  TotalsStoreCredit: "totals_store_credit",
  UpdatedAt: "updatedat",
  UpsellAmount: "upsell_amount",
  WrongProduct: "wrongproduct",
} as const;

export type ReturnPhysicalField =
  (typeof ReturnPhysicalField)[keyof typeof ReturnPhysicalField];

export const RETURN_PHYSICAL_FIELDS = fieldValues(ReturnPhysicalField);

export const ReturnPaymentIntentField = {
  AmountUsd: "amount_usd",
  PaymentIntentId: "payment_intent_id",
  PaymentIntentType: "payment_intent_type",
  ReturnCreatedAt: "return_created_at",
  ReturnId: "return_id",
  ReturnProcessedAt: "return_processed_at",
  ReturnUpdatedAt: "return_updated_at",
  TeamId: "team_id",
} as const;

export type ReturnPaymentIntentField =
  (typeof ReturnPaymentIntentField)[keyof typeof ReturnPaymentIntentField];

export const RETURN_PAYMENT_INTENT_FIELDS = fieldValues(
  ReturnPaymentIntentField,
);

export const ReturnPaymentIntentPhysicalField = {
  AmountUsd: "amount_usd",
  PaymentIntentId: "payment_intent_id",
  PaymentIntentType: "payment_intent_type",
  ReturnCreatedAt: "return_created_at",
  ReturnId: "return_id",
  ReturnProcessedAt: "return_processed_at",
  ReturnUpdatedAt: "return_updated_at",
  TeamId: "team_id",
} as const;

export type ReturnPaymentIntentPhysicalField =
  (typeof ReturnPaymentIntentPhysicalField)[keyof typeof ReturnPaymentIntentPhysicalField];

export const RETURN_PAYMENT_INTENT_PHYSICAL_FIELDS = fieldValues(
  ReturnPaymentIntentPhysicalField,
);

export const ReturnLineItemMultipleChoiceField = {
  Answer: "answer",
  LineItemId: "line_item_id",
  ProductId: "product_id",
  QuestionIndex: "question_index",
  QuestionText: "question_text",
  ReturnCreatedAt: "return_created_at",
  ReturnId: "return_id",
  TeamId: "team_id",
  VariantId: "variant_id",
} as const;

export type ReturnLineItemMultipleChoiceField =
  (typeof ReturnLineItemMultipleChoiceField)[keyof typeof ReturnLineItemMultipleChoiceField];

export const RETURN_LINE_ITEM_MULTIPLE_CHOICE_FIELDS = fieldValues(
  ReturnLineItemMultipleChoiceField,
);

export const ReturnLineItemMultipleChoicePhysicalField = {
  Answer: "answer",
  LineItemId: "line_item_id",
  ProductId: "product_id",
  QuestionIndex: "question_index",
  QuestionText: "question",
  ReturnCreatedAt: "return_created_at",
  ReturnId: "return_id",
  TeamId: "team_id",
  VariantId: "variant_id",
} as const;

export type ReturnLineItemMultipleChoicePhysicalField =
  (typeof ReturnLineItemMultipleChoicePhysicalField)[keyof typeof ReturnLineItemMultipleChoicePhysicalField];

export const RETURN_LINE_ITEM_MULTIPLE_CHOICE_PHYSICAL_FIELDS = fieldValues(
  ReturnLineItemMultipleChoicePhysicalField,
);

export const ReturnShipmentTrackingDetailField = {
  CarrierCode: "carrier_code",
  EventDate: "event_date",
  EventDescription: "event_description",
  EventIndex: "event_index",
  EventMessage: "event_message",
  EventStatus: "event_status",
  EventStatusDetail: "event_status_detail",
  LocationCity: "location_city",
  LocationCountry: "location_country",
  LocationState: "location_state",
  ReturnId: "return_id",
  ShipmentId: "shipment_id",
  TeamId: "team_id",
} as const;

export type ReturnShipmentTrackingDetailField =
  (typeof ReturnShipmentTrackingDetailField)[keyof typeof ReturnShipmentTrackingDetailField];

export const RETURN_SHIPMENT_TRACKING_DETAIL_FIELDS = fieldValues(
  ReturnShipmentTrackingDetailField,
);

export const ReturnShipmentTrackingDetailPhysicalField = {
  CarrierCode: "carrier_code",
  EventDate: "datetime",
  EventDescription: "description",
  EventIndex: "tracking_detail_index",
  EventMessage: "message",
  EventStatus: "status",
  EventStatusDetail: "status_detail",
  LocationCity: "tracking_location_city",
  LocationCountry: "tracking_location_country",
  LocationState: "tracking_location_state",
  ReturnId: "return_id",
  ShipmentId: "shipment_id",
  TeamId: "team_id",
} as const;

export type ReturnShipmentTrackingDetailPhysicalField =
  (typeof ReturnShipmentTrackingDetailPhysicalField)[keyof typeof ReturnShipmentTrackingDetailPhysicalField];

export const RETURN_SHIPMENT_TRACKING_DETAIL_PHYSICAL_FIELDS = fieldValues(
  ReturnShipmentTrackingDetailPhysicalField,
);

export const OrderFulfillmentField = {
  Carrier: "carrier",
  CreatedAt: "created_at",
  FulfillmentId: "fulfillment_id",
  FulfillmentIndex: "fulfillment_index",
  FulfillmentOrderId: "fulfillment_order_id",
  LocationId: "location_id",
  Name: "name",
  OrderId: "order_id",
  Service: "service",
  ShipmentStatus: "shipment_status",
  Status: "status",
  TeamId: "team_id",
  TrackingNumber: "tracking_number",
  TrackingUrl: "tracking_url",
  UpdatedAt: "updated_at",
} as const;

export type OrderFulfillmentField =
  (typeof OrderFulfillmentField)[keyof typeof OrderFulfillmentField];

export const ORDER_FULFILLMENT_FIELDS = fieldValues(OrderFulfillmentField);

export const OrderFulfillmentPhysicalField = {
  Carrier: "fulfillment_tracking_company",
  CreatedAt: "created_at",
  FulfillmentId: "fulfillment_id",
  FulfillmentIndex: "fulfillment_index",
  FulfillmentOrderId: "fulfillment_order_id",
  LocationId: "location_id",
  Name: "fulfillment_name",
  OrderId: "order_id",
  Service: "fulfillment_service",
  ShipmentStatus: "fulfillment_shipment_status",
  Status: "fulfillment_status",
  TeamId: "team_id",
  TrackingNumber: "fulfillment_tracking_number",
  TrackingUrl: "fulfillment_tracking_url",
  UpdatedAt: "fulfillment_updated_at",
} as const;

export type OrderFulfillmentPhysicalField =
  (typeof OrderFulfillmentPhysicalField)[keyof typeof OrderFulfillmentPhysicalField];

export const ORDER_FULFILLMENT_PHYSICAL_FIELDS = fieldValues(
  OrderFulfillmentPhysicalField,
);

export const OrderFulfillmentLineItemField = {
  FulfillmentIndex: "fulfillment_index",
  FulfillmentLineItemId: "fulfillment_line_item_id",
  LineItemIndex: "line_item_index",
  OrderId: "order_id",
  Quantity: "quantity",
  Sku: "sku",
  TeamId: "team_id",
  Title: "title",
  VariantTitle: "variant_title",
} as const;

export type OrderFulfillmentLineItemField =
  (typeof OrderFulfillmentLineItemField)[keyof typeof OrderFulfillmentLineItemField];

export const ORDER_FULFILLMENT_LINE_ITEM_FIELDS = fieldValues(
  OrderFulfillmentLineItemField,
);

export const OrderFulfillmentLineItemPhysicalField = {
  FulfillmentIndex: "fulfillment_index",
  FulfillmentLineItemId: "fulfillment_line_item_id",
  LineItemIndex: "fulfillment_line_item_index",
  OrderId: "order_id",
  Quantity: "quantity",
  Sku: "sku",
  TeamId: "team_id",
  Title: "title",
  VariantTitle: "variant_title",
} as const;

export type OrderFulfillmentLineItemPhysicalField =
  (typeof OrderFulfillmentLineItemPhysicalField)[keyof typeof OrderFulfillmentLineItemPhysicalField];

export const ORDER_FULFILLMENT_LINE_ITEM_PHYSICAL_FIELDS = fieldValues(
  OrderFulfillmentLineItemPhysicalField,
);

export const ReturnLineItemField = {
  AdjustmentValue: "adjustment_value",
  AiReturnReasonSummary: "ai_return_reason_summary",
  AdvancedExchangeItems: "advanced_exchange_items",
  ApprovedAt: "approved_at",
  Damaged: "damaged",
  ExchangeFor: "exchange_for",
  ExchangeGroupItem: "exchange_group_item",
  ExchangeValue: "exchange_value",
  GreenReturn: "green_return",
  InputAnswers: "input_answers",
  IsFlagged: "is_flagged",
  IsManualReview: "is_manual_review",
  LineItemId: "line_item_id",
  OrderId: "order_id",
  OrderLineItemId: "order_line_item_id",
  Price: "price",
  ProcessedAt: "processed_at",
  ProductId: "product_id",
  ProductStatus: "product_status",
  ProductTitle: "product_title",
  Quantity: "quantity",
  Reason: "reason",
  Reasons: "reasons",
  RefundValue: "refund_value",
  RejectMessage: "reject_message",
  ReturnId: "return_id",
  ReturnToMerchantDeliveredDate: "return_to_merchant_delivered_date",
  ReturnType: "return_type",
  Sku: "sku",
  Strategy: "strategy",
  StoreCreditValue: "store_credit_value",
  TaxValue: "tax_value",
  TeamId: "team_id",
  TotalProductValue: "total_product_value",
  ValueBeforeTaxAndAdjustments: "value_before_tax_and_adjustments",
  VariantId: "variant_id",
  VariantTitle: "variant_title",
  WrongProduct: "wrong_product",
} as const;

export type ReturnLineItemField =
  (typeof ReturnLineItemField)[keyof typeof ReturnLineItemField];

export const RETURN_LINE_ITEM_FIELDS = fieldValues(ReturnLineItemField);

export const ReturnLineItemPhysicalField = {
  AdjustmentValue: "analytics_totals_product_adjustment",
  AiReturnReasonSummary: "ai_return_reason_summary",
  AdvancedExchangeItems: "advanced_exchange_items",
  ApprovedAt: "approved_at",
  Damaged: "damaged",
  ExchangeFor: "exchange_for",
  ExchangeGroupItem: "exchange_group_item",
  ExchangeValue: "analytics_totals_product_exchange_value",
  GreenReturn: "green_return",
  InputAnswers: "input_answers",
  IsFlagged: "is_flagged",
  IsManualReview: "is_manual_review",
  LineItemId: "line_item_id",
  OrderId: "order_id",
  OrderLineItemId: "order_line_item_id",
  Price: "price",
  ProcessedAt: "processed_at",
  ProductId: "product_id",
  ProductStatus: "product_status",
  ProductTitle: "product_title",
  Quantity: "quantity",
  Reason: "reason",
  Reasons: "reasons",
  RefundValue: "analytics_totals_product_refund_value",
  RejectMessage: "reject_message",
  ReturnId: "return_id",
  ReturnToMerchantDeliveredDate: "return_to_merchant_delivered_date",
  ReturnType: "return_type",
  Sku: "sku",
  Strategy: "strategy",
  StoreCreditValue: "analytics_totals_product_store_credit_value",
  TaxValue: "analytics_totals_product_tax",
  TeamId: "team_id",
  TotalProductValue: "analytics_totals_total_product_value",
  ValueBeforeTaxAndAdjustments:
    "analytics_totals_product_value_no_tax_no_adjustment",
  VariantId: "variant_id",
  VariantTitle: "variant_title",
  WrongProduct: "wrong_product",
} as const;

export type ReturnLineItemPhysicalField =
  (typeof ReturnLineItemPhysicalField)[keyof typeof ReturnLineItemPhysicalField];

export const RETURN_LINE_ITEM_PHYSICAL_FIELDS = fieldValues(
  ReturnLineItemPhysicalField,
);

export const FulfillmentGroupShipmentSurchargeField = {
  Amount: "amount",
  CapturedHeightInInches: "captured_height_in_inches",
  CapturedLengthInInches: "captured_length_in_inches",
  CapturedWeightInOunces: "captured_weight_in_ounces",
  CapturedWidthInInches: "captured_width_in_inches",
  Category: "category",
  Currency: "currency",
  Description: "description",
  FulfillmentGroupId: "fulfillment_group_id",
  ShipmentId: "shipment_id",
  Source: "source",
  SurchargeDate: "surcharge_date",
  TeamId: "team_id",
  UniqueIdentifier: "unique_identifier",
} as const;

export type FulfillmentGroupShipmentSurchargeField =
  (typeof FulfillmentGroupShipmentSurchargeField)[keyof typeof FulfillmentGroupShipmentSurchargeField];

export const FULFILLMENT_GROUP_SHIPMENT_SURCHARGE_FIELDS = fieldValues(
  FulfillmentGroupShipmentSurchargeField,
);

export const FulfillmentGroupShipmentSurchargePhysicalField = {
  Amount: "amount",
  CapturedHeightInInches: "captured_height_in_inches",
  CapturedLengthInInches: "captured_length_in_inches",
  CapturedWeightInOunces: "captured_weight_in_ounces",
  CapturedWidthInInches: "captured_width_in_inches",
  Category: "category",
  Currency: "currency",
  Description: "description",
  FulfillmentGroupId: "mongo_id",
  ShipmentId: "shipment_id",
  Source: "source",
  SurchargeDate: "surcharge_date",
  TeamId: "team_id",
  UniqueIdentifier: "unique_identifier",
} as const;

export type FulfillmentGroupShipmentSurchargePhysicalField =
  (typeof FulfillmentGroupShipmentSurchargePhysicalField)[keyof typeof FulfillmentGroupShipmentSurchargePhysicalField];

export const FULFILLMENT_GROUP_SHIPMENT_SURCHARGE_PHYSICAL_FIELDS = fieldValues(
  FulfillmentGroupShipmentSurchargePhysicalField,
);

export const FulfillmentGroupFulfillmentOrderField = {
  FulfillmentGroupId: "fulfillment_group_id",
  FulfillmentOrderId: "fulfillment_order_id",
  RedoOrderId: "redo_order_id",
  TeamId: "team_id",
} as const;

export type FulfillmentGroupFulfillmentOrderField =
  (typeof FulfillmentGroupFulfillmentOrderField)[keyof typeof FulfillmentGroupFulfillmentOrderField];

export const FULFILLMENT_GROUP_FULFILLMENT_ORDER_FIELDS = fieldValues(
  FulfillmentGroupFulfillmentOrderField,
);

export const FulfillmentGroupFulfillmentOrderPhysicalField = {
  FulfillmentGroupId: "mongo_id",
  FulfillmentOrderId: "fulfillment_order_id",
  RedoOrderId: "redo_order_id",
  TeamId: "team",
} as const;

export type FulfillmentGroupFulfillmentOrderPhysicalField =
  (typeof FulfillmentGroupFulfillmentOrderPhysicalField)[keyof typeof FulfillmentGroupFulfillmentOrderPhysicalField];

export const FULFILLMENT_GROUP_FULFILLMENT_ORDER_PHYSICAL_FIELDS = fieldValues(
  FulfillmentGroupFulfillmentOrderPhysicalField,
);

export const ReturnShipmentField = {
  Carrier: "carrier",
  TrackingNumber: "tracking_number",
  LabelStatus: "label_status",
  RedoShipmentId: "redo_shipment_id",
  ReturnId: "return_id",
  Service: "service",
  ShipmentCreatedAt: "shipment_created_at",
  ShipmentId: "shipment_id",
  TeamId: "team_id",
  Weight: "weight",
} as const;

export type ReturnShipmentField =
  (typeof ReturnShipmentField)[keyof typeof ReturnShipmentField];

export const RETURN_SHIPMENT_FIELDS = fieldValues(ReturnShipmentField);

export const ReturnShipmentPhysicalField = {
  Carrier: "carrier",
  TrackingNumber: "tracking_code",
  LabelStatus: "label_status",
  RedoShipmentId: "redo_shipment_id",
  ReturnId: "return_id",
  Service: "service",
  ShipmentCreatedAt: "shipment_created_at",
  ShipmentId: "shipment_id",
  TeamId: "team_id",
  Weight: "weight",
} as const;

export type ReturnShipmentPhysicalField =
  (typeof ReturnShipmentPhysicalField)[keyof typeof ReturnShipmentPhysicalField];

export const RETURN_SHIPMENT_PHYSICAL_FIELDS = fieldValues(
  ReturnShipmentPhysicalField,
);

export const MarketingOutreachEventField = {
  Team: "team",
  EventId: "event_id",
  EventType: "event_type",
  Channel: "channel",
  Category: "category",
  AutomationId: "automation_id",
  CampaignId: "campaign_id",
  EventDate: "event_date",
  Customer: "customer",
  CarrierCostMicros: "carrier_cost_micros",
  IsDuplicate: "is_duplicate",
} as const;

export type MarketingOutreachEventField =
  (typeof MarketingOutreachEventField)[keyof typeof MarketingOutreachEventField];

export const MARKETING_OUTREACH_EVENT_FIELDS = fieldValues(
  MarketingOutreachEventField,
);

// Physical columns on STAGING.MONGODB.STG_CUSTOMER_EVENTS_OUTREACH. Snowflake
// folds unquoted identifiers to upper case, so these lower-case names match the
// upper-case stored columns (e.g. _ID, EVENTTYPE, COSTCARRIERFEEMICROS).
export const MarketingOutreachEventPhysicalField = {
  Team: "team",
  EventId: "_id",
  EventType: "eventtype",
  Channel: "channel",
  Category: "category",
  AutomationId: "automation",
  CampaignId: "campaign",
  EventDate: "timestamp",
  Customer: "customer",
  CarrierCostMicros: "costcarrierfeemicros",
  IsDuplicate: "is_duplicate",
} as const;

export type MarketingOutreachEventPhysicalField =
  (typeof MarketingOutreachEventPhysicalField)[keyof typeof MarketingOutreachEventPhysicalField];

export const MARKETING_OUTREACH_EVENT_PHYSICAL_FIELDS = fieldValues(
  MarketingOutreachEventPhysicalField,
);

export const CampaignField = {
  Team: "team",
  CampaignId: "campaign_id",
  CampaignName: "campaign_name",
  Channel: "channel",
  ScheduledAt: "scheduled_at",
  FinishedAt: "finished_at",
} as const;

export type CampaignField = (typeof CampaignField)[keyof typeof CampaignField];

export const CAMPAIGN_FIELDS = fieldValues(CampaignField);

// Physical columns on STAGING.COCKROACHDB.STG_CAMPAIGNS (case-insensitive
// match to the upper-case stored columns MONGO_ID, TEAMID, NAME, etc.).
export const CampaignPhysicalField = {
  Team: "teamid",
  CampaignId: "mongo_id",
  CampaignName: "name",
  Channel: "channel",
  ScheduledAt: "scheduledat",
  FinishedAt: "finishedat",
} as const;

export type CampaignPhysicalField =
  (typeof CampaignPhysicalField)[keyof typeof CampaignPhysicalField];

export const CAMPAIGN_PHYSICAL_FIELDS = fieldValues(CampaignPhysicalField);

export const AutomationField = {
  Team: "team",
  AutomationId: "automation_id",
  AutomationName: "automation_name",
  Category: "category",
  Enabled: "enabled",
  CreatedAt: "created_at",
} as const;

export type AutomationField =
  (typeof AutomationField)[keyof typeof AutomationField];

export const AUTOMATION_FIELDS = fieldValues(AutomationField);

// Physical columns on STAGING.COCKROACHDB.STG_ADVANCED_FLOWS (case-insensitive
// match to the upper-case stored columns MONGO_ID, TEAM_ID, etc.).
export const AutomationPhysicalField = {
  Team: "team_id",
  AutomationId: "mongo_id",
  AutomationName: "name",
  Category: "category",
  Enabled: "enabled",
  CreatedAt: "created_at",
} as const;

export type AutomationPhysicalField =
  (typeof AutomationPhysicalField)[keyof typeof AutomationPhysicalField];

export const AUTOMATION_PHYSICAL_FIELDS = fieldValues(AutomationPhysicalField);

export const PostPurchaseUpsellEventField = {
  CustomerEventId: "customer_event_id",
  TeamId: "team_id",
  UpsellPageId: "upsell_page_id",
  EventType: "event_type",
  EventTimestamp: "event_timestamp",
  NodeId: "node_id",
  ReferenceId: "reference_id",
  AcceptedProductId: "accepted_product_id",
  AcceptedProductTitle: "accepted_product_title",
  AcceptedProductVariantId: "accepted_product_variant_id",
  RevenueInShopCurrency: "revenue_in_shop_currency",
} as const;

export type PostPurchaseUpsellEventField =
  (typeof PostPurchaseUpsellEventField)[keyof typeof PostPurchaseUpsellEventField];

export const POST_PURCHASE_UPSELL_EVENT_FIELDS = fieldValues(
  PostPurchaseUpsellEventField,
);

// Physical columns on STAGING.MONGODB.STG_CUSTOMER_EVENTS_POST_PURCHASE. The
// staged model already exposes lower_snake_case columns, so the semantic and
// physical names match.
export const PostPurchaseUpsellEventPhysicalField = {
  CustomerEventId: "customer_event_id",
  TeamId: "team_id",
  UpsellPageId: "upsell_page_id",
  EventType: "event_type",
  EventTimestamp: "event_timestamp",
  NodeId: "node_id",
  ReferenceId: "reference_id",
  AcceptedProductId: "accepted_product_id",
  AcceptedProductTitle: "accepted_product_title",
  AcceptedProductVariantId: "accepted_product_variant_id",
  RevenueInShopCurrency: "revenue_in_shop_currency",
} as const;

export type PostPurchaseUpsellEventPhysicalField =
  (typeof PostPurchaseUpsellEventPhysicalField)[keyof typeof PostPurchaseUpsellEventPhysicalField];

export const POST_PURCHASE_UPSELL_EVENT_PHYSICAL_FIELDS = fieldValues(
  PostPurchaseUpsellEventPhysicalField,
);

export const PostPurchaseUpsellPageField = {
  UpsellPageId: "upsell_page_id",
  TeamId: "team_id",
  PageName: "page_name",
  LayoutType: "layout_type",
  CreatedAt: "created_at",
  UpdatedAt: "updated_at",
} as const;

export type PostPurchaseUpsellPageField =
  (typeof PostPurchaseUpsellPageField)[keyof typeof PostPurchaseUpsellPageField];

export const POST_PURCHASE_UPSELL_PAGE_FIELDS = fieldValues(
  PostPurchaseUpsellPageField,
);

// Physical columns on STAGING.COCKROACHDB.STG_UPSELL_PAGES. The staged model
// already exposes lower_snake_case columns, so semantic and physical names
// match, except UpsellPageId, which reads mongo_id because
// STG_CUSTOMER_EVENTS_POST_PURCHASE still stores Mongo ids.
export const PostPurchaseUpsellPagePhysicalField = {
  UpsellPageId: "mongo_id",
  TeamId: "team_id",
  PageName: "page_name",
  LayoutType: "layout_type",
  CreatedAt: "created_at",
  UpdatedAt: "updated_at",
} as const;

export type PostPurchaseUpsellPagePhysicalField =
  (typeof PostPurchaseUpsellPagePhysicalField)[keyof typeof PostPurchaseUpsellPagePhysicalField];

export const POST_PURCHASE_UPSELL_PAGE_PHYSICAL_FIELDS = fieldValues(
  PostPurchaseUpsellPagePhysicalField,
);

export const ProductField = {
  AccountId: "account_id",
  Currency: "currency",
  DeletedAt: "deleted_at",
  PhysicalItem: "physical_item",
  Price: "price",
  ProductId: "product_id",
  Sku: "sku",
  Source: "source",
  SourceId: "source_id",
  SourceVariantId: "source_variant_id",
  StockoutDate: "stockout_date",
  Title: "title",
  Upc: "upc",
  Vendor: "vendor",
} as const;

export type ProductField = (typeof ProductField)[keyof typeof ProductField];

export const PRODUCT_FIELDS = fieldValues(ProductField);

// Physical columns on STAGING.COCKROACHDB.STG_CRDB_PRODUCTS. The staged model
// already exposes lower_snake_case columns, so semantic and physical names
// match.
export const ProductPhysicalField = {
  AccountId: "account_id",
  Currency: "currency",
  DeletedAt: "deleted_at",
  PhysicalItem: "physical_item",
  Price: "price",
  ProductId: "product_id",
  Sku: "sku",
  Source: "source",
  SourceId: "source_id",
  SourceVariantId: "source_variant_id",
  StockoutDate: "stockout_date",
  Title: "title",
  Upc: "upc",
  Vendor: "vendor",
} as const;

export type ProductPhysicalField =
  (typeof ProductPhysicalField)[keyof typeof ProductPhysicalField];

export const PRODUCT_PHYSICAL_FIELDS = fieldValues(ProductPhysicalField);

export const InventoryLevelField = {
  DeletedAt: "deleted_at",
  InventoryLevelId: "inventory_level_id",
  LastCountedAt: "last_counted_at",
  LocationAccountId: "location_account_id",
  MaxQuantity: "max_quantity",
  ProductAccountId: "product_account_id",
  ProductId: "product_id",
  Quantity: "quantity",
  StorageLocationId: "storage_location_id",
} as const;

export type InventoryLevelField =
  (typeof InventoryLevelField)[keyof typeof InventoryLevelField];

export const INVENTORY_LEVEL_FIELDS = fieldValues(InventoryLevelField);

// Physical columns on STAGING.COCKROACHDB.STG_CRDB_INVENTORY_LEVEL. The staged
// model already exposes lower_snake_case columns, so semantic and physical
// names match.
export const InventoryLevelPhysicalField = {
  DeletedAt: "deleted_at",
  InventoryLevelId: "inventory_level_id",
  LastCountedAt: "last_counted_at",
  LocationAccountId: "location_account_id",
  MaxQuantity: "max_quantity",
  ProductAccountId: "product_account_id",
  ProductId: "product_id",
  Quantity: "quantity",
  StorageLocationId: "storage_location_id",
} as const;

export type InventoryLevelPhysicalField =
  (typeof InventoryLevelPhysicalField)[keyof typeof InventoryLevelPhysicalField];

export const INVENTORY_LEVEL_PHYSICAL_FIELDS = fieldValues(
  InventoryLevelPhysicalField,
);

export const StorageLocationField = {
  AccountId: "account_id",
  IsSellable: "is_sellable",
  LocationId: "location_id",
  Name: "name",
  Source: "source",
  StorageLocationId: "storage_location_id",
  Stype: "stype",
} as const;

export type StorageLocationField =
  (typeof StorageLocationField)[keyof typeof StorageLocationField];

export const STORAGE_LOCATION_FIELDS = fieldValues(StorageLocationField);

// Physical columns on STAGING.COCKROACHDB.STG_CRDB_STORAGE_LOCATION. The staged
// model exposes the CockroachDB primary key as `id`, so the semantic
// storage_location_id maps to the physical id; the rest match.
export const StorageLocationPhysicalField = {
  AccountId: "account_id",
  IsSellable: "is_sellable",
  LocationId: "location_id",
  Name: "name",
  Source: "source",
  StorageLocationId: "id",
  Stype: "stype",
} as const;

export type StorageLocationPhysicalField =
  (typeof StorageLocationPhysicalField)[keyof typeof StorageLocationPhysicalField];

export const STORAGE_LOCATION_PHYSICAL_FIELDS = fieldValues(
  StorageLocationPhysicalField,
);

// Columns on STAGING.COCKROACHDB.STG_CRDB_LOCATION. The staged model exposes the
// CockroachDB primary key as `location_id`; the rest are lower_snake_case and
// match their semantic names.
export const LocationField = {
  AccountId: "account_id",
  City: "city",
  Country: "country",
  CountryCode: "country_code",
  CreatedAt: "created_at",
  ExternalId: "external_id",
  FulfillsOnlineOrders: "fulfills_online_orders",
  IsActive: "is_active",
  IsReturnsCenter: "is_returns_center",
  LocationId: "location_id",
  Name: "name",
  PostalCode: "postal_code",
  Provider: "provider",
  ReturnsCenterId: "returns_center_id",
  ShipsInventory: "ships_inventory",
  State: "state",
  UpdatedAt: "updated_at",
} as const;

export type LocationField = (typeof LocationField)[keyof typeof LocationField];

export const LOCATION_FIELDS = fieldValues(LocationField);

export const LocationPhysicalField = {
  AccountId: "account_id",
  City: "city",
  Country: "country",
  CountryCode: "country_code",
  CreatedAt: "created_at",
  ExternalId: "external_id",
  FulfillsOnlineOrders: "fulfills_online_orders",
  IsActive: "is_active",
  IsReturnsCenter: "is_returns_center",
  LocationId: "location_id",
  Name: "name",
  PostalCode: "postal_code",
  Provider: "provider",
  ReturnsCenterId: "returns_center_id",
  ShipsInventory: "ships_inventory",
  State: "state",
  UpdatedAt: "updated_at",
} as const;

export type LocationPhysicalField =
  (typeof LocationPhysicalField)[keyof typeof LocationPhysicalField];

export const LOCATION_PHYSICAL_FIELDS = fieldValues(LocationPhysicalField);

export const BillField = {
  AccountId: "account_id",
  BalanceEnd: "balance_end",
  BalanceStart: "balance_start",
  BillId: "bill_id",
  CreatedAt: "created_at",
  EndTime: "end_time",
  LegacyId: "legacy_id",
  StartTime: "start_time",
  Status: "status",
  Type: "type",
  UpdatedAt: "updated_at",
} as const;

export type BillField = (typeof BillField)[keyof typeof BillField];

export const BILL_FIELDS = fieldValues(BillField);

export const BillPhysicalField = {
  AccountId: "account_id",
  BalanceEnd: "balance_end",
  BalanceStart: "balance_start",
  BillId: "bill_id",
  CreatedAt: "created_at",
  EndTime: "end_time",
  LegacyId: "legacy_id",
  StartTime: "start_time",
  Status: "status",
  Type: "type",
  UpdatedAt: "updated_at",
} as const;

export type BillPhysicalField =
  (typeof BillPhysicalField)[keyof typeof BillPhysicalField];

export const BILL_PHYSICAL_FIELDS = fieldValues(BillPhysicalField);

export const BillingChargeField = {
  AccountId: "account_id",
  Amount: "amount",
  BillId: "bill_id",
  BillingChargeId: "billing_charge_id",
  BillingMethod: "billing_method",
  Channel: "channel",
  ChargeSubtype: "charge_subtype",
  CreatedAt: "created_at",
  Currency: "currency",
  Damaged: "damaged",
  Description: "description",
  EndTime: "end_time",
  FulfillmentGroupId: "fulfillment_group_id",
  LegacyId: "legacy_id",
  LineItemName: "line_item_name",
  OrderId: "order_id",
  Product: "product",
  RecoveredRevenue: "recovered_revenue",
  ReturnId: "return_id",
  RevenueSharePercentage: "revenue_share_percentage",
  ShipmentId: "shipment_id",
  StartTime: "start_time",
  Type: "type",
  WrongProduct: "wrong_product",
} as const;

export type BillingChargeField =
  (typeof BillingChargeField)[keyof typeof BillingChargeField];

export const BILLING_CHARGE_FIELDS = fieldValues(BillingChargeField);

export const BillingChargePhysicalField = {
  AccountId: "account_id",
  Amount: "amount",
  BillId: "bill_id",
  BillingChargeId: "billing_charge_id",
  BillingMethod: "billing_method",
  Channel: "channel",
  ChargeSubtype: "charge_subtype",
  CreatedAt: "created_at",
  Currency: "currency",
  Damaged: "damaged",
  Description: "description",
  EndTime: "end_time",
  FulfillmentGroupId: "fulfillment_group_id",
  LegacyId: "legacy_id",
  LineItemName: "line_item_name",
  OrderId: "order_id",
  Product: "product",
  RecoveredRevenue: "recovered_revenue",
  ReturnId: "return_id",
  RevenueSharePercentage: "revenue_share_percentage",
  ShipmentId: "shipment_id",
  StartTime: "start_time",
  Type: "type",
  WrongProduct: "wrong_product",
} as const;

export type BillingChargePhysicalField =
  (typeof BillingChargePhysicalField)[keyof typeof BillingChargePhysicalField];

export const BILLING_CHARGE_PHYSICAL_FIELDS = fieldValues(
  BillingChargePhysicalField,
);

export const RescuePanelMetricsField = {
  DatePeriod: "date_period",
  NumEngagements: "num_engagements",
  NumImpressions: "num_impressions",
  NumOrders: "num_orders",
  RevenueAmount: "revenue_amount",
  RevenueCurrency: "revenue_currency",
  TeamId: "team_id",
  TeamName: "team_name",
} as const;

export type RescuePanelMetricsField =
  (typeof RescuePanelMetricsField)[keyof typeof RescuePanelMetricsField];

export const RESCUE_PANEL_METRICS_FIELDS = fieldValues(RescuePanelMetricsField);

// Physical columns on REDO.CHECKOUT_OPTIMIZATION.RESCUE_PANEL_METRICS. The dbt
// rollup exposes lower_snake_case columns, so semantic and physical names match.
export const RescuePanelMetricsPhysicalField = {
  DatePeriod: "date_period",
  NumEngagements: "num_engagements",
  NumImpressions: "num_impressions",
  NumOrders: "num_orders",
  RevenueAmount: "revenue_amount",
  RevenueCurrency: "revenue_currency",
  TeamId: "team_id",
  TeamName: "team_name",
} as const;

export type RescuePanelMetricsPhysicalField =
  (typeof RescuePanelMetricsPhysicalField)[keyof typeof RescuePanelMetricsPhysicalField];

export const RESCUE_PANEL_METRICS_PHYSICAL_FIELDS = fieldValues(
  RescuePanelMetricsPhysicalField,
);

export const RescuePanelAttributedOrdersField = {
  OrderDate: "order_date",
  OrderId: "order_id",
  TeamId: "team_id",
} as const;

export type RescuePanelAttributedOrdersField =
  (typeof RescuePanelAttributedOrdersField)[keyof typeof RescuePanelAttributedOrdersField];

export const RESCUE_PANEL_ATTRIBUTED_ORDERS_FIELDS = fieldValues(
  RescuePanelAttributedOrdersField,
);

// Physical columns on REDO.JOIN_TABLES.RESCUE_PANEL_ATTRIBUTED_ORDERS. The dbt
// join table exposes lower_snake_case columns, so semantic and physical names
// match. revenue_amount/revenue_currency exist physically but are not mapped
// here (the Order grain owns revenue).
export const RescuePanelAttributedOrdersPhysicalField = {
  OrderDate: "order_date",
  OrderId: "order_id",
  TeamId: "team_id",
} as const;

export type RescuePanelAttributedOrdersPhysicalField =
  (typeof RescuePanelAttributedOrdersPhysicalField)[keyof typeof RescuePanelAttributedOrdersPhysicalField];

export const RESCUE_PANEL_ATTRIBUTED_ORDERS_PHYSICAL_FIELDS = fieldValues(
  RescuePanelAttributedOrdersPhysicalField,
);

/** STG_SHIPPING_CLAIMS projects lower_snake_case, so semantic and physical names match. */
export const ShippingClaimField = {
  Carrier: "carrier",
  CarrierClaimSubmittedAt: "carrier_claim_submitted_at",
  CreatedAt: "created_at",
  DenialReason: "denial_reason",
  FulfillmentGroupId: "fulfillment_group_id",
  GoodsValue: "goods_value",
  Id: "_id",
  InsuredValue: "insured_value",
  MerchantRefundAmount: "merchant_refund_amount",
  Reason: "reason",
  RefundedAt: "refunded_at",
  RefundIssued: "refund_issued",
  RequestedBy: "requested_by",
  ReturnId: "return_id",
  Source: "source",
  Status: "status",
  SubmittedAt: "submitted_at",
  Team: "team",
  TrackingCode: "tracking_code",
  Type: "type",
  UpdatedAt: "updated_at",
} as const;

export type ShippingClaimField =
  (typeof ShippingClaimField)[keyof typeof ShippingClaimField];

export const SHIPPING_CLAIM_FIELDS = fieldValues(ShippingClaimField);

export const ShippingClaimPhysicalField = {
  Carrier: "carrier",
  CarrierClaimSubmittedAt: "carrier_claim_submitted_at",
  CreatedAt: "created_at",
  DenialReason: "denial_reason",
  FulfillmentGroupId: "fulfillment_group_id",
  GoodsValue: "goods_value",
  Id: "_id",
  InsuredValue: "insured_value",
  MerchantRefundAmount: "merchant_refund_amount",
  Reason: "reason",
  RefundedAt: "refunded_at",
  RefundIssued: "refund_issued",
  RequestedBy: "requested_by",
  ReturnId: "return_id",
  Source: "source",
  Status: "status",
  SubmittedAt: "submitted_at",
  Team: "team",
  TrackingCode: "tracking_code",
  Type: "type",
  UpdatedAt: "updated_at",
} as const;

export type ShippingClaimPhysicalField =
  (typeof ShippingClaimPhysicalField)[keyof typeof ShippingClaimPhysicalField];

export const SHIPPING_CLAIM_PHYSICAL_FIELDS = fieldValues(
  ShippingClaimPhysicalField,
);

/**
 * Item rows have no team column. draft_return_id is the draft Mongo id.
 */
export const DraftReturnPendingReturnItemField = {
  DraftReturnCreatedAt: "draft_return_created_at",
  DraftReturnId: "draft_return_id",
  DraftReturnUpdatedAt: "draft_return_updated_at",
  HasSuggestedExchangeResult: "has_suggested_exchange_result",
  PendingReturnItemId: "pending_return_item_id",
  SuggestedExchangedItem: "suggested_exchanged_item",
} as const;

export type DraftReturnPendingReturnItemField =
  (typeof DraftReturnPendingReturnItemField)[keyof typeof DraftReturnPendingReturnItemField];

export const DRAFT_RETURN_PENDING_RETURN_ITEM_FIELDS = fieldValues(
  DraftReturnPendingReturnItemField,
);

export const DraftReturnPendingReturnItemPhysicalField = {
  DraftReturnCreatedAt: "draft_return_created_at",
  DraftReturnMongoId: "draft_return_mongo_id",
  DraftReturnUpdatedAt: "draft_return_updated_at",
  HasSuggestedExchangeResult: "has_suggested_exchange_result",
  PendingReturnItemId: "pending_return_item_id",
  SuggestedExchangedItem: "suggested_exchanged_item",
} as const;

export type DraftReturnPendingReturnItemPhysicalField =
  (typeof DraftReturnPendingReturnItemPhysicalField)[keyof typeof DraftReturnPendingReturnItemPhysicalField];

export const DRAFT_RETURN_PENDING_RETURN_ITEM_PHYSICAL_FIELDS = fieldValues(
  DraftReturnPendingReturnItemPhysicalField,
);

/**
 * STG_CONVERSATIONS projects one lowercase concatenation, createdat, so the
 * semantic name created_at differs from the physical column.
 */
export const ConversationField = {
  Archived: "archived",
  ConversationId: "conversation_id",
  CreatedAt: "created_at",
  CustomerId: "customer_id",
  IsSpam: "is_spam",
  Platform: "platform",
  Status: "status",
  TeamId: "team_id",
} as const;

export type ConversationField =
  (typeof ConversationField)[keyof typeof ConversationField];

export const CONVERSATION_FIELDS = fieldValues(ConversationField);

export const ConversationPhysicalField = {
  Archived: "archived",
  ConversationId: "conversation_id",
  CreatedAt: "createdat",
  CustomerId: "customer_id",
  IsSpam: "is_spam",
  Platform: "platform",
  Status: "status",
  TeamId: "team_id",
} as const;

export type ConversationPhysicalField =
  (typeof ConversationPhysicalField)[keyof typeof ConversationPhysicalField];

export const CONVERSATION_PHYSICAL_FIELDS = fieldValues(
  ConversationPhysicalField,
);

/**
 * STG_CONVERSATION_MESSAGES names every projected column the same way the
 * semantic layer does, so no name differs from its physical column.
 */
export const ConversationMessageField = {
  ConversationId: "conversation_id",
  IsSystemMessage: "is_system_message",
  MessageCreatedAt: "message_created_at",
  MessageId: "message_id",
  MessageIndex: "message_index",
  TeamId: "team_id",
  Type: "type",
  Visibility: "visibility",
} as const;

export type ConversationMessageField =
  (typeof ConversationMessageField)[keyof typeof ConversationMessageField];

export const CONVERSATION_MESSAGE_FIELDS = fieldValues(
  ConversationMessageField,
);

export const ConversationMessagePhysicalField = {
  ConversationId: "conversation_id",
  IsSystemMessage: "is_system_message",
  MessageCreatedAt: "message_created_at",
  MessageId: "message_id",
  MessageIndex: "message_index",
  TeamId: "team_id",
  Type: "type",
  Visibility: "visibility",
} as const;

export type ConversationMessagePhysicalField =
  (typeof ConversationMessagePhysicalField)[keyof typeof ConversationMessagePhysicalField];

export const CONVERSATION_MESSAGE_PHYSICAL_FIELDS = fieldValues(
  ConversationMessagePhysicalField,
);

/**
 * STG_RETURN_TAGS projects lower_snake_case, so semantic and physical names
 * match.
 */
export const ReturnTagField = {
  ReturnId: "return_id",
  TagIndex: "tag_index",
  TagName: "tag_name",
  Team: "team",
} as const;

export type ReturnTagField =
  (typeof ReturnTagField)[keyof typeof ReturnTagField];

export const RETURN_TAG_FIELDS = fieldValues(ReturnTagField);

export const ReturnTagPhysicalField = {
  ReturnId: "return_id",
  TagIndex: "tag_index",
  TagName: "tag_name",
  Team: "team",
} as const;

export type ReturnTagPhysicalField =
  (typeof ReturnTagPhysicalField)[keyof typeof ReturnTagPhysicalField];

export const RETURN_TAG_PHYSICAL_FIELDS = fieldValues(ReturnTagPhysicalField);

/**
 * STG_SATISFACTION_RESPONSES projects the Mongo camelCase names unchanged for
 * two columns, so createdAt lands as createdat and the semantic name differs.
 */
export const SatisfactionResponseField = {
  Channel: "channel",
  ConversationId: "conversation_id",
  CreatedAt: "created_at",
  Id: "_id",
  Rating: "rating",
  SendAt: "send_at",
  Status: "status",
  SupportTicketTopicId: "support_ticket_topic_id",
  TeamId: "team_id",
} as const;

export type SatisfactionResponseField =
  (typeof SatisfactionResponseField)[keyof typeof SatisfactionResponseField];

export const SATISFACTION_RESPONSE_FIELDS = fieldValues(
  SatisfactionResponseField,
);

export const SatisfactionResponsePhysicalField = {
  Channel: "channel",
  ConversationId: "conversation_id",
  CreatedAt: "createdat",
  Id: "_id",
  Rating: "rating",
  SendAt: "sendat",
  Status: "status",
  SupportTicketTopicId: "support_ticket_topic_id",
  TeamId: "team_id",
} as const;

export type SatisfactionResponsePhysicalField =
  (typeof SatisfactionResponsePhysicalField)[keyof typeof SatisfactionResponsePhysicalField];

export const SATISFACTION_RESPONSE_PHYSICAL_FIELDS = fieldValues(
  SatisfactionResponsePhysicalField,
);

/**
 * STG_ORDER_EXTENDED_WARRANTIES projects lower_snake_case aliases off the
 * flattened array element, so semantic and physical names match.
 */
export const OrderExtendedWarrantyField = {
  AppliedDiscount: "applied_discount",
  CollectionHandle: "collection_handle",
  DaysExtended: "days_extended",
  ExtendedWarrantyId: "extended_warranty_id",
  ExtendedWarrantyIndex: "extended_warranty_index",
  OrderId: "order_id",
  PricePaid: "price_paid",
  Quantity: "quantity",
  TeamId: "team_id",
  VariantId: "variant_id",
  VoidedQuantity: "voided_quantity",
  WarrantyActivationDelay: "warranty_activation_delay",
} as const;

export type OrderExtendedWarrantyField =
  (typeof OrderExtendedWarrantyField)[keyof typeof OrderExtendedWarrantyField];

export const ORDER_EXTENDED_WARRANTY_FIELDS = fieldValues(
  OrderExtendedWarrantyField,
);

export const OrderExtendedWarrantyPhysicalField = {
  AppliedDiscount: "applied_discount",
  CollectionHandle: "collection_handle",
  DaysExtended: "days_extended",
  ExtendedWarrantyId: "extended_warranty_id",
  ExtendedWarrantyIndex: "extended_warranty_index",
  OrderId: "order_id",
  PricePaid: "price_paid",
  Quantity: "quantity",
  TeamId: "team_id",
  VariantId: "variant_id",
  VoidedQuantity: "voided_quantity",
  WarrantyActivationDelay: "warranty_activation_delay",
} as const;

export type OrderExtendedWarrantyPhysicalField =
  (typeof OrderExtendedWarrantyPhysicalField)[keyof typeof OrderExtendedWarrantyPhysicalField];

export const ORDER_EXTENDED_WARRANTY_PHYSICAL_FIELDS = fieldValues(
  OrderExtendedWarrantyPhysicalField,
);

/**
 * STG_CUSTOMER_EVENTS_FAILED_DISCOUNT_RESCUE names every projected column the
 * same way the semantic layer does, so no name differs from its physical
 * column. Five projected columns are deliberately left undeclared.
 */
export const FailedDiscountRescueEventField = {
  AttemptedCode: "attempted_code",
  CheckoutToken: "checkout_token",
  CustomerEventId: "customer_event_id",
  EventTimestamp: "event_timestamp",
  EventType: "event_type",
  IsDuplicate: "is_duplicate",
  TeamId: "team_id",
} as const;

export type FailedDiscountRescueEventField =
  (typeof FailedDiscountRescueEventField)[keyof typeof FailedDiscountRescueEventField];

export const FAILED_DISCOUNT_RESCUE_EVENT_FIELDS = fieldValues(
  FailedDiscountRescueEventField,
);

export const FailedDiscountRescueEventPhysicalField = {
  AttemptedCode: "attempted_code",
  CheckoutToken: "checkout_token",
  CustomerEventId: "customer_event_id",
  EventTimestamp: "event_timestamp",
  EventType: "event_type",
  IsDuplicate: "is_duplicate",
  TeamId: "team_id",
} as const;

export type FailedDiscountRescueEventPhysicalField =
  (typeof FailedDiscountRescueEventPhysicalField)[keyof typeof FailedDiscountRescueEventPhysicalField];

export const FAILED_DISCOUNT_RESCUE_EVENT_PHYSICAL_FIELDS = fieldValues(
  FailedDiscountRescueEventPhysicalField,
);

/**
 * STG_CUSTOMER_TAGS names every declared column the same way the semantic layer
 * does. Two projected columns are deliberately left undeclared.
 */
export const CustomerTagField = {
  CustomerId: "customer_id",
  Tag: "tag",
  Team: "team",
} as const;

export type CustomerTagField =
  (typeof CustomerTagField)[keyof typeof CustomerTagField];

export const CUSTOMER_TAG_FIELDS = fieldValues(CustomerTagField);

export const CustomerTagPhysicalField = {
  CustomerId: "customer_id",
  Tag: "tag",
  Team: "team",
} as const;

export type CustomerTagPhysicalField =
  (typeof CustomerTagPhysicalField)[keyof typeof CustomerTagPhysicalField];

export const CUSTOMER_TAG_PHYSICAL_FIELDS = fieldValues(
  CustomerTagPhysicalField,
);

/**
 * STG_CUSTOM_CUSTOMER_EVENTS names every declared column the same way the
 * semantic layer does. Six projected columns are left undeclared.
 */
export const CustomCustomerEventField = {
  CreatedAt: "created_at",
  CustomerId: "customer_id",
  EventId: "event_id",
  EventName: "event_name",
  EventTimestamp: "event_timestamp",
  Producer: "producer",
  TeamId: "team_id",
  UniqueId: "unique_id",
  Value: "value",
  ValueCurrency: "value_currency",
} as const;

export type CustomCustomerEventField =
  (typeof CustomCustomerEventField)[keyof typeof CustomCustomerEventField];

export const CUSTOM_CUSTOMER_EVENT_FIELDS = fieldValues(
  CustomCustomerEventField,
);

export const CustomCustomerEventPhysicalField = {
  CreatedAt: "created_at",
  CustomerId: "customer_id",
  EventId: "event_id",
  EventName: "event_name",
  EventTimestamp: "event_timestamp",
  Producer: "producer",
  TeamId: "team_id",
  UniqueId: "unique_id",
  Value: "value",
  ValueCurrency: "value_currency",
} as const;

export type CustomCustomerEventPhysicalField =
  (typeof CustomCustomerEventPhysicalField)[keyof typeof CustomCustomerEventPhysicalField];

export const CUSTOM_CUSTOMER_EVENT_PHYSICAL_FIELDS = fieldValues(
  CustomCustomerEventPhysicalField,
);

/**
 * STG_CUSTOMER_CUSTOM_FIELDS names every declared column the same way the
 * semantic layer does. Only sync_to_snowflake_at is left undeclared.
 */
export const CustomerCustomFieldField = {
  BooleanValue: "boolean_value",
  CustomFieldIndex: "custom_field_index",
  CustomFieldValueIndex: "custom_field_value_index",
  CustomerId: "customer_id",
  DataType: "data_type",
  DateValue: "date_value",
  DeclarationId: "declaration_id",
  NumberValue: "number_value",
  StringValue: "string_value",
  TeamId: "team_id",
} as const;

export type CustomerCustomFieldField =
  (typeof CustomerCustomFieldField)[keyof typeof CustomerCustomFieldField];

export const CUSTOMER_CUSTOM_FIELD_FIELDS = fieldValues(
  CustomerCustomFieldField,
);

export const CustomerCustomFieldPhysicalField = {
  BooleanValue: "boolean_value",
  CustomFieldIndex: "custom_field_index",
  CustomFieldValueIndex: "custom_field_value_index",
  CustomerId: "customer_id",
  DataType: "data_type",
  DateValue: "date_value",
  DeclarationId: "declaration_id",
  NumberValue: "number_value",
  StringValue: "string_value",
  TeamId: "team_id",
} as const;

export type CustomerCustomFieldPhysicalField =
  (typeof CustomerCustomFieldPhysicalField)[keyof typeof CustomerCustomFieldPhysicalField];

export const CUSTOMER_CUSTOM_FIELD_PHYSICAL_FIELDS = fieldValues(
  CustomerCustomFieldPhysicalField,
);

/**
 * STG_PRODUCT_TAGS, minus sync_to_snowflake_at, legacy_id and updatedat,
 * which are pipeline metadata and the parent product's own data.
 */
export const ProductTagField = {
  ProductId: "product_id",
  Tag: "tag",
  TeamId: "team_id",
} as const;
export type ProductTagField =
  (typeof ProductTagField)[keyof typeof ProductTagField];
export const PRODUCT_TAG_FIELDS = fieldValues(ProductTagField);
export const ProductTagPhysicalField = {
  ProductId: "product_id",
  Tag: "tag",
  TeamId: "team_id",
} as const;
export type ProductTagPhysicalField =
  (typeof ProductTagPhysicalField)[keyof typeof ProductTagPhysicalField];
export const PRODUCT_TAG_PHYSICAL_FIELDS = fieldValues(ProductTagPhysicalField);
/**
 * STG_FULFILLMENT_GROUP_TRACKING_EVENTS, the scan's own columns plus the two
 * tenant columns the parcel carries: the parcel's carrier, tracking code and
 * status are the shipment's data.
 */
export const FulfillmentGroupTrackingEventField = {
  AssignedFulfillmentService: "assigned_fulfillment_service",
  EventAt: "event_at",
  EventCity: "event_city",
  EventCountry: "event_country",
  EventMessage: "event_message",
  EventState: "event_state",
  EventStatus: "event_status",
  FulfillmentGroupCreatedAt: "fulfillment_group_created_at",
  FulfillmentGroupId: "fulfillment_group_id",
  TeamId: "team_id",
  TrackingEventIndex: "tracking_event_index",
} as const;
export type FulfillmentGroupTrackingEventField =
  (typeof FulfillmentGroupTrackingEventField)[keyof typeof FulfillmentGroupTrackingEventField];
export const FULFILLMENT_GROUP_TRACKING_EVENT_FIELDS = fieldValues(
  FulfillmentGroupTrackingEventField,
);
export const FulfillmentGroupTrackingEventPhysicalField = {
  AssignedFulfillmentService: "assigned_fulfillment_service",
  EventAt: "event_at",
  EventCity: "event_city",
  EventCountry: "event_country",
  EventMessage: "event_message",
  EventState: "event_state",
  EventStatus: "event_status",
  FulfillmentGroupCreatedAt: "fulfillment_group_created_at",
  FulfillmentGroupId: "mongo_id",
  TeamId: "team_id",
  TrackingEventIndex: "tracking_event_index",
} as const;
export type FulfillmentGroupTrackingEventPhysicalField =
  (typeof FulfillmentGroupTrackingEventPhysicalField)[keyof typeof FulfillmentGroupTrackingEventPhysicalField];
export const FULFILLMENT_GROUP_TRACKING_EVENT_PHYSICAL_FIELDS = fieldValues(
  FulfillmentGroupTrackingEventPhysicalField,
);
/**
 * STG_PRODUCT_COLLECTIONS, minus sync_to_snowflake_at and the two legacy
 * identifier forms, which restate identifiers already declared here.
 */
export const ProductCollectionField = {
  CollectionId: "collection_id",
  CollectionName: "collection_name",
  ProductId: "product_id",
  TeamId: "team_id",
} as const;
export type ProductCollectionField =
  (typeof ProductCollectionField)[keyof typeof ProductCollectionField];
export const PRODUCT_COLLECTION_FIELDS = fieldValues(ProductCollectionField);
export const ProductCollectionPhysicalField = {
  CollectionId: "collection_id",
  CollectionName: "collection_name",
  ProductId: "product_id",
  TeamId: "team_id",
} as const;
export type ProductCollectionPhysicalField =
  (typeof ProductCollectionPhysicalField)[keyof typeof ProductCollectionPhysicalField];
export const PRODUCT_COLLECTION_PHYSICAL_FIELDS = fieldValues(
  ProductCollectionPhysicalField,
);
/**
 * STG_REVIEWS, minus the shopper's name and email, minus updated_at, and
 * minus the untyped review_fields array that holds the score.
 */
export const ProductReviewField = {
  CreatedAt: "created_at",
  ReviewId: "review_id",
  ShopifyProductId: "shopify_product_id",
  Status: "status",
  TeamId: "team_id",
  VerifiedPurchase: "verified_purchase",
} as const;
export type ProductReviewField =
  (typeof ProductReviewField)[keyof typeof ProductReviewField];
export const PRODUCT_REVIEW_FIELDS = fieldValues(ProductReviewField);
export const ProductReviewPhysicalField = {
  CreatedAt: "created_at",
  ReviewId: "review_id",
  ShopifyProductId: "shopify_product_id",
  Status: "status",
  TeamId: "team_id",
  VerifiedPurchase: "verified_purchase",
} as const;
export type ProductReviewPhysicalField =
  (typeof ProductReviewPhysicalField)[keyof typeof ProductReviewPhysicalField];
export const PRODUCT_REVIEW_PHYSICAL_FIELDS = fieldValues(
  ProductReviewPhysicalField,
);
/**
 * STG_DAILY_USER_SESSION_SUMMARIES names every column it projects; this
 * grain declares all of them.
 */
export const DailyShopperSessionField = {
  SessionsWithCheckoutCompleted: "sessions_with_checkout_completed",
  SessionsWithConciergeAttributedCheckoutCompleted:
    "sessions_with_concierge_attributed_checkout_completed",
  SessionsWithConciergeInteracted: "sessions_with_concierge_interacted",
  SessionsWithConciergePresent: "sessions_with_concierge_present",
  SessionsWithSubsequentCheckoutCompletedSession:
    "sessions_with_subsequent_checkout_completed_session",
  SignedInUserSessions: "signed_in_user_sessions",
  SummaryDate: "summary_date",
  TeamId: "team_id",
  TotalUserSessions: "total_user_sessions",
  UserSessionsSummaryId: "user_sessions_summary_id",
} as const;
export type DailyShopperSessionField =
  (typeof DailyShopperSessionField)[keyof typeof DailyShopperSessionField];
export const DAILY_SHOPPER_SESSION_FIELDS = fieldValues(
  DailyShopperSessionField,
);
export const DailyShopperSessionPhysicalField = {
  SessionsWithCheckoutCompleted: "sessions_with_checkout_completed",
  SessionsWithConciergeAttributedCheckoutCompleted:
    "sessions_with_concierge_attributed_checkout_completed",
  SessionsWithConciergeInteracted: "sessions_with_concierge_interacted",
  SessionsWithConciergePresent: "sessions_with_concierge_present",
  SessionsWithSubsequentCheckoutCompletedSession:
    "sessions_with_subsequent_checkout_completed_session",
  SignedInUserSessions: "signed_in_user_sessions",
  SummaryDate: "summary_date",
  TeamId: "team_id",
  TotalUserSessions: "total_user_sessions",
  UserSessionsSummaryId: "user_sessions_summary_id",
} as const;
export type DailyShopperSessionPhysicalField =
  (typeof DailyShopperSessionPhysicalField)[keyof typeof DailyShopperSessionPhysicalField];
export const DAILY_SHOPPER_SESSION_PHYSICAL_FIELDS = fieldValues(
  DailyShopperSessionPhysicalField,
);
/**
 * STG_MARKETING_AGENT_DECISIONS, minus the generated message copy and the
 * shopper, thread and trigger-provenance identifiers.
 */
export const MarketingAgentDecisionField = {
  Channel: "channel",
  DecisionId: "decision_id",
  DecisionType: "decision_type",
  DiscountPercentage: "discount_percentage",
  FinishedAt: "finished_at",
  TeamId: "team_id",
  TriggerType: "trigger_type",
  TriggeredAt: "triggered_at",
} as const;
export type MarketingAgentDecisionField =
  (typeof MarketingAgentDecisionField)[keyof typeof MarketingAgentDecisionField];
export const MARKETING_AGENT_DECISION_FIELDS = fieldValues(
  MarketingAgentDecisionField,
);
export const MarketingAgentDecisionPhysicalField = {
  Channel: "channel",
  DecisionId: "decision_id",
  DecisionType: "decision_type",
  DiscountPercentage: "discount_percentage",
  FinishedAt: "finished_at",
  TeamId: "team_id",
  TriggerType: "trigger_type",
  TriggeredAt: "triggered_at",
} as const;
export type MarketingAgentDecisionPhysicalField =
  (typeof MarketingAgentDecisionPhysicalField)[keyof typeof MarketingAgentDecisionPhysicalField];
export const MARKETING_AGENT_DECISION_PHYSICAL_FIELDS = fieldValues(
  MarketingAgentDecisionPhysicalField,
);
/**
 * STG_MARKETING_AGENT_DECISION_TAGS, the label and its two keys. The shopper
 * identifier the model also projects is deliberately not declared.
 */
export const MarketingAgentDecisionTagField = {
  DecisionId: "decision_id",
  Tag: "tag",
  TeamId: "team_id",
} as const;
export type MarketingAgentDecisionTagField =
  (typeof MarketingAgentDecisionTagField)[keyof typeof MarketingAgentDecisionTagField];
export const MARKETING_AGENT_DECISION_TAG_FIELDS = fieldValues(
  MarketingAgentDecisionTagField,
);
export const MarketingAgentDecisionTagPhysicalField = {
  DecisionId: "decision_id",
  Tag: "tag",
  TeamId: "team_id",
} as const;
export type MarketingAgentDecisionTagPhysicalField =
  (typeof MarketingAgentDecisionTagPhysicalField)[keyof typeof MarketingAgentDecisionTagPhysicalField];
export const MARKETING_AGENT_DECISION_TAG_PHYSICAL_FIELDS = fieldValues(
  MarketingAgentDecisionTagPhysicalField,
);
/**
 * STG_CUSTOMER_GROUP_MEMBERSHIPS, minus sync_to_snowflake_at and updatedat,
 * which is the parent customer document's timestamp and not the row's own.
 */
export const CustomerGroupMembershipField = {
  AddedAt: "added_at",
  CustomerId: "customer_id",
  GroupId: "group_id",
  MembershipIndex: "membership_index",
  Team: "team",
} as const;
export type CustomerGroupMembershipField =
  (typeof CustomerGroupMembershipField)[keyof typeof CustomerGroupMembershipField];
export const CUSTOMER_GROUP_MEMBERSHIP_FIELDS = fieldValues(
  CustomerGroupMembershipField,
);
export const CustomerGroupMembershipPhysicalField = {
  AddedAt: "added_at",
  CustomerId: "customer_id",
  GroupId: "group_id",
  MembershipIndex: "customer_group_membership_index",
  Team: "team",
} as const;
export type CustomerGroupMembershipPhysicalField =
  (typeof CustomerGroupMembershipPhysicalField)[keyof typeof CustomerGroupMembershipPhysicalField];
export const CUSTOMER_GROUP_MEMBERSHIP_PHYSICAL_FIELDS = fieldValues(
  CustomerGroupMembershipPhysicalField,
);
/**
 * STG_CAMPAIGN_RECIPIENTS, the queue entry's own plumbing only: the whole
 * pre-aggregated metric block and every cost column stay out.
 */
export const CampaignRecipientField = {
  AutomationId: "automation_id",
  CampaignId: "campaign_id",
  Category: "category",
  Channel: "channel",
  CreatedAt: "created_at",
  RecipientId: "recipient_id",
  Status: "status",
  TeamId: "team_id",
} as const;
export type CampaignRecipientField =
  (typeof CampaignRecipientField)[keyof typeof CampaignRecipientField];
export const CAMPAIGN_RECIPIENT_FIELDS = fieldValues(CampaignRecipientField);
export const CampaignRecipientPhysicalField = {
  AutomationId: "automationid",
  CampaignId: "campaignid",
  Category: "category",
  Channel: "channel",
  CreatedAt: "createdat",
  RecipientId: "_id",
  Status: "status",
  TeamId: "teamid",
} as const;
export type CampaignRecipientPhysicalField =
  (typeof CampaignRecipientPhysicalField)[keyof typeof CampaignRecipientPhysicalField];
export const CAMPAIGN_RECIPIENT_PHYSICAL_FIELDS = fieldValues(
  CampaignRecipientPhysicalField,
);
export const CheckoutDeliveryOptionField = {
  CostAmount: "cost_amount",
  CostCurrency: "cost_currency",
  CustomerEventId: "customer_event_id",
  DeliveryOptionGroupIndex: "delivery_option_group_index",
  DeliveryOptionIndex: "delivery_option_index",
  PromisedDeliveryDate: "promised_delivery_date",
  PromisedDeliveryDays: "promised_delivery_days",
  TeamId: "team_id",
} as const;
export type CheckoutDeliveryOptionField =
  (typeof CheckoutDeliveryOptionField)[keyof typeof CheckoutDeliveryOptionField];
export const CHECKOUT_DELIVERY_OPTION_FIELDS = fieldValues(
  CheckoutDeliveryOptionField,
);
export const CheckoutDeliveryOptionPhysicalField = {
  CostAmount: "cost_amount",
  CostCurrency: "cost_currency",
  CustomerEventId: "customer_event_id",
  DeliveryOptionGroupIndex: "delivery_option_group_index",
  DeliveryOptionIndex: "delivery_option_index",
  PromisedDeliveryDate: "promised_delivery_date",
  PromisedDeliveryDays: "promised_delivery_days",
  TeamId: "team_id",
} as const;
export type CheckoutDeliveryOptionPhysicalField =
  (typeof CheckoutDeliveryOptionPhysicalField)[keyof typeof CheckoutDeliveryOptionPhysicalField];
export const CHECKOUT_DELIVERY_OPTION_PHYSICAL_FIELDS = fieldValues(
  CheckoutDeliveryOptionPhysicalField,
);
export const CheckoutDeliveryGroupField = {
  CheckoutExperienceId: "checkout_experience_id",
  CombinationsExplored: "combinations_explored",
  CustomerEventId: "customer_event_id",
  DeliveryOptionGroupIndex: "delivery_option_group_index",
  RecordedAt: "recorded_at",
  TeamId: "team_id",
} as const;
export type CheckoutDeliveryGroupField =
  (typeof CheckoutDeliveryGroupField)[keyof typeof CheckoutDeliveryGroupField];
export const CHECKOUT_DELIVERY_GROUP_FIELDS = fieldValues(
  CheckoutDeliveryGroupField,
);
export const CheckoutDeliveryGroupPhysicalField = {
  CheckoutExperienceId: "checkout_experience_id",
  CombinationsExplored: "mercury_combinations_explored",
  CustomerEventId: "customer_event_id",
  DeliveryOptionGroupIndex: "delivery_option_group_index",
  RecordedAt: "timestamp",
  TeamId: "team_id",
} as const;
export type CheckoutDeliveryGroupPhysicalField =
  (typeof CheckoutDeliveryGroupPhysicalField)[keyof typeof CheckoutDeliveryGroupPhysicalField];
export const CHECKOUT_DELIVERY_GROUP_PHYSICAL_FIELDS = fieldValues(
  CheckoutDeliveryGroupPhysicalField,
);
export const CheckoutInfoChangeField = {
  CheckoutToken: "checkout_token",
  CreatedAt: "created_at",
  Customer: "customer",
  CustomerEventId: "customer_event_id",
  CustomerOrderCountStatus: "customer_order_count_status",
  EventTimestamp: "event_timestamp",
  SessionId: "session_id",
  TeamId: "team_id",
  TreatmentIdStatus: "treatment_id_status",
  UpdatedAt: "updated_at",
} as const;
export type CheckoutInfoChangeField =
  (typeof CheckoutInfoChangeField)[keyof typeof CheckoutInfoChangeField];
export const CHECKOUT_INFO_CHANGE_FIELDS = fieldValues(CheckoutInfoChangeField);
// Physical columns on STAGING.MONGODB.STG_CUSTOMER_EVENTS_CHECKOUT_INFO_CHANGED,
// which keeps Mongo's spellings (_id, team, createdat, sessionid, updatedat).
export const CheckoutInfoChangePhysicalField = {
  CheckoutToken: "checkout_token",
  CreatedAt: "createdat",
  Customer: "customer",
  CustomerEventId: "_id",
  CustomerOrderCountStatus: "customer_order_count_status",
  EventTimestamp: "timestamp",
  SessionId: "sessionid",
  TeamId: "team",
  TreatmentIdStatus: "treatment_id_status",
  UpdatedAt: "updatedat",
} as const;
export type CheckoutInfoChangePhysicalField =
  (typeof CheckoutInfoChangePhysicalField)[keyof typeof CheckoutInfoChangePhysicalField];
export const CHECKOUT_INFO_CHANGE_PHYSICAL_FIELDS = fieldValues(
  CheckoutInfoChangePhysicalField,
);
export const CheckoutShippingStepEventField = {
  AddressFingerprint: "address_fingerprint",
  AssignedTreatmentIds: "assigned_treatment_ids",
  CartDiscount: "order_totals_discount_amount",
  CartSubtotal: "order_totals_subtotal_price",
  CartTotal: "order_totals_total_price",
  CustomerEventId: "customer_event_id",
  EventTimestamp: "event_timestamp",
  LocalTimestamp: "local_date_time_iso_date_time",
  SessionId: "session_id",
  ShippingCountry: "shipping_country",
  ShippingProvince: "shipping_province",
  ShopCurrency: "shop_currency",
  TeamId: "team_id",
} as const;
export type CheckoutShippingStepEventField =
  (typeof CheckoutShippingStepEventField)[keyof typeof CheckoutShippingStepEventField];
export const CHECKOUT_SHIPPING_STEP_EVENT_FIELDS = fieldValues(
  CheckoutShippingStepEventField,
);
export const CheckoutShippingStepEventPhysicalField = {
  AddressFingerprint: "address_fingerprint",
  AssignedTreatmentIds: "assigned_treatment_ids",
  CartDiscount: "order_totals_discount_amount",
  CartSubtotal: "order_totals_subtotal_price",
  CartTotal: "order_totals_total_price",
  CustomerEventId: "customer_event_id",
  EventTimestamp: "event_timestamp",
  LocalTimestamp: "local_date_time_iso_date_time",
  SessionId: "session_id",
  ShippingCountry: "shipping_country",
  ShippingProvince: "shipping_province",
  ShopCurrency: "shop_currency",
  TeamId: "team_id",
} as const;
export type CheckoutShippingStepEventPhysicalField =
  (typeof CheckoutShippingStepEventPhysicalField)[keyof typeof CheckoutShippingStepEventPhysicalField];
export const CHECKOUT_SHIPPING_STEP_EVENT_PHYSICAL_FIELDS = fieldValues(
  CheckoutShippingStepEventPhysicalField,
);
export const CheckoutOrderCreatedEventField = {
  AddressFingerprint: "address_fingerprint",
  AssignedTreatmentIds: "assigned_treatment_ids",
  Currency: "currency",
  CustomerEventId: "customer_event_id",
  EventTimestamp: "event_timestamp",
  OrderId: "order_id",
  ShippingCountry: "shipping_country",
  ShippingProvince: "shipping_province",
  ShippingRevenue: "shipping_revenue",
  TeamId: "team_id",
  TotalPrice: "total_price",
} as const;
export type CheckoutOrderCreatedEventField =
  (typeof CheckoutOrderCreatedEventField)[keyof typeof CheckoutOrderCreatedEventField];
export const CHECKOUT_ORDER_CREATED_EVENT_FIELDS = fieldValues(
  CheckoutOrderCreatedEventField,
);
export const CheckoutOrderCreatedEventPhysicalField = {
  AddressFingerprint: "address_fingerprint",
  AssignedTreatmentIds: "assigned_treatment_ids",
  Currency: "currency",
  CustomerEventId: "customer_event_id",
  EventTimestamp: "event_timestamp",
  OrderId: "order_id",
  ShippingCountry: "shipping_country",
  ShippingProvince: "shipping_province",
  ShippingRevenue: "shipping_revenue",
  TeamId: "team_id",
  TotalPrice: "total_price",
} as const;
export type CheckoutOrderCreatedEventPhysicalField =
  (typeof CheckoutOrderCreatedEventPhysicalField)[keyof typeof CheckoutOrderCreatedEventPhysicalField];
export const CHECKOUT_ORDER_CREATED_EVENT_PHYSICAL_FIELDS = fieldValues(
  CheckoutOrderCreatedEventPhysicalField,
);
export const CheckoutWidgetInteractionField = {
  CheckoutExperienceId: "checkout_experience_id",
  EventIndex: "event_index",
  EventType: "event_type",
  OrderCreatedAt: "shopify_created_at",
  OrderId: "order_id",
  ProductIds: "product_ids",
  ProductVariantId: "product_variant_id",
  Quantity: "quantity",
  TeamId: "team_id",
  WidgetId: "widget_id",
} as const;
export type CheckoutWidgetInteractionField =
  (typeof CheckoutWidgetInteractionField)[keyof typeof CheckoutWidgetInteractionField];
export const CHECKOUT_WIDGET_INTERACTION_FIELDS = fieldValues(
  CheckoutWidgetInteractionField,
);
export const CheckoutWidgetInteractionPhysicalField = {
  CheckoutExperienceId: "checkout_experience_id",
  EventIndex: "event_index",
  EventType: "event_type",
  OrderCreatedAt: "shopify_created_at",
  OrderId: "order_id",
  ProductIds: "product_ids",
  ProductVariantId: "product_variant_id",
  Quantity: "quantity",
  TeamId: "team_id",
  WidgetId: "widget_id",
} as const;
export type CheckoutWidgetInteractionPhysicalField =
  (typeof CheckoutWidgetInteractionPhysicalField)[keyof typeof CheckoutWidgetInteractionPhysicalField];
export const CHECKOUT_WIDGET_INTERACTION_PHYSICAL_FIELDS = fieldValues(
  CheckoutWidgetInteractionPhysicalField,
);
export const PostPurchaseSplitAssignmentField = {
  CustomerEventId: "customer_event_id",
  EventType: "eventtype",
  NodeId: "node_id",
  RecordedAt: "recorded_at",
  ReferenceId: "reference_id",
  SplitId: "split_id",
  SplitIndex: "split_index",
  TeamId: "team_id",
  TreatmentId: "treatment_id",
} as const;
export type PostPurchaseSplitAssignmentField =
  (typeof PostPurchaseSplitAssignmentField)[keyof typeof PostPurchaseSplitAssignmentField];
export const POST_PURCHASE_SPLIT_ASSIGNMENT_FIELDS = fieldValues(
  PostPurchaseSplitAssignmentField,
);
export const PostPurchaseSplitAssignmentPhysicalField = {
  CustomerEventId: "customer_event_id",
  EventType: "eventtype",
  NodeId: "node_id",
  RecordedAt: "timestamp",
  ReferenceId: "reference_id",
  SplitId: "split_id",
  SplitIndex: "split_index",
  TeamId: "team_id",
  TreatmentId: "treatment_id",
} as const;
export type PostPurchaseSplitAssignmentPhysicalField =
  (typeof PostPurchaseSplitAssignmentPhysicalField)[keyof typeof PostPurchaseSplitAssignmentPhysicalField];
export const POST_PURCHASE_SPLIT_ASSIGNMENT_PHYSICAL_FIELDS = fieldValues(
  PostPurchaseSplitAssignmentPhysicalField,
);
/**
 * The marketing-list slice of STG_CUSTOMER_EVENTS. That model carries every
 * customer event type, so only the shared identifiers and the four list
 * columns are declared here; the grain narrows to the three list actions
 * with a scope filter.
 */
export const MarketingSubscriptionEventField = {
  CreatedAt: "createdat",
  Customer: "customer",
  CustomerEventId: "_id",
  Email: "email",
  EntryPoint: "entry_point",
  EventTimestamp: "timestamp",
  EventType: "eventtype",
  IsDuplicate: "is_duplicate",
  PhoneNumber: "phone_number",
  SubscriptionType: "subscription_type",
  Team: "team",
  UpdatedAt: "updatedat",
} as const;

export type MarketingSubscriptionEventField =
  (typeof MarketingSubscriptionEventField)[keyof typeof MarketingSubscriptionEventField];

export const MARKETING_SUBSCRIPTION_EVENT_FIELDS = fieldValues(
  MarketingSubscriptionEventField,
);

export const MarketingSubscriptionEventPhysicalField = {
  CreatedAt: "createdat",
  Customer: "customer",
  CustomerEventId: "_id",
  Email: "email",
  EntryPoint: "entry_point",
  EventTimestamp: "timestamp",
  EventType: "eventtype",
  IsDuplicate: "is_duplicate",
  PhoneNumber: "phone_number",
  SubscriptionType: "subscription_type",
  Team: "team",
  UpdatedAt: "updatedat",
} as const;

export type MarketingSubscriptionEventPhysicalField =
  (typeof MarketingSubscriptionEventPhysicalField)[keyof typeof MarketingSubscriptionEventPhysicalField];

export const MARKETING_SUBSCRIPTION_EVENT_PHYSICAL_FIELDS = fieldValues(
  MarketingSubscriptionEventPhysicalField,
);

/**
 * The back-in-stock slice of STG_CUSTOMER_EVENTS. That model carries every
 * customer event type, so only the shared identifiers and the bare product
 * and variant references are declared here; the grain narrows to the two
 * back-in-stock event types with a scope filter.
 */
export const BackInStockEventField = {
  CreatedAt: "createdat",
  Customer: "customer",
  CustomerEventId: "_id",
  EventTimestamp: "timestamp",
  EventType: "eventtype",
  ProductId: "product_id",
  Team: "team",
  UpdatedAt: "updatedat",
  VariantId: "variant_id",
} as const;

export type BackInStockEventField =
  (typeof BackInStockEventField)[keyof typeof BackInStockEventField];

export const BACK_IN_STOCK_EVENT_FIELDS = fieldValues(BackInStockEventField);

export const BackInStockEventPhysicalField = {
  CreatedAt: "createdat",
  Customer: "customer",
  CustomerEventId: "_id",
  EventTimestamp: "timestamp",
  EventType: "eventtype",
  ProductId: "product_id",
  Team: "team",
  UpdatedAt: "updatedat",
  VariantId: "variant_id",
} as const;

export type BackInStockEventPhysicalField =
  (typeof BackInStockEventPhysicalField)[keyof typeof BackInStockEventPhysicalField];

export const BACK_IN_STOCK_EVENT_PHYSICAL_FIELDS = fieldValues(
  BackInStockEventPhysicalField,
);

/**
 * The collection-view slice of STG_CUSTOMER_EVENTS. That model carries every
 * customer event type, so only the shared identifiers and the two collection
 * columns are declared here; the grain narrows to COLLECTION_VIEWED with a
 * scope filter.
 */
export const CollectionViewEventField = {
  CollectionId: "collection_id",
  CollectionTitle: "collection_title",
  CreatedAt: "createdat",
  Customer: "customer",
  CustomerEventId: "_id",
  EventTimestamp: "timestamp",
  EventType: "eventtype",
  SessionId: "sessionid",
  StorefrontProvider: "storefront_provider",
  Team: "team",
  UpdatedAt: "updatedat",
} as const;

export type CollectionViewEventField =
  (typeof CollectionViewEventField)[keyof typeof CollectionViewEventField];

export const COLLECTION_VIEW_EVENT_FIELDS = fieldValues(
  CollectionViewEventField,
);

export const CollectionViewEventPhysicalField = {
  CollectionId: "collection_id",
  CollectionTitle: "collection_title",
  CreatedAt: "createdat",
  Customer: "customer",
  CustomerEventId: "_id",
  EventTimestamp: "timestamp",
  EventType: "eventtype",
  SessionId: "sessionid",
  StorefrontProvider: "storefront_provider",
  Team: "team",
  UpdatedAt: "updatedat",
} as const;

export type CollectionViewEventPhysicalField =
  (typeof CollectionViewEventPhysicalField)[keyof typeof CollectionViewEventPhysicalField];

export const COLLECTION_VIEW_EVENT_PHYSICAL_FIELDS = fieldValues(
  CollectionViewEventPhysicalField,
);

/**
 * The product-detail-view slice of STG_CUSTOMER_EVENTS. That model carries
 * every customer event type, so only the shared identifiers and the
 * product-variant columns are declared here; the grain narrows to
 * PRODUCT_VIEWED with a scope filter.
 */
export const ProductViewEventField = {
  CreatedAt: "createdat",
  Customer: "customer",
  CustomerEventId: "_id",
  EventTimestamp: "timestamp",
  EventType: "eventtype",
  IsDuplicate: "is_duplicate",
  ProductId: "product_variant_product_id",
  ProductTitle: "product_variant_product_title",
  SessionId: "sessionid",
  Sku: "product_variant_sku",
  StorefrontProvider: "storefront_provider",
  Team: "team",
  UpdatedAt: "updatedat",
  VariantId: "product_variant_id",
  VariantTitle: "product_variant_title",
} as const;

export type ProductViewEventField =
  (typeof ProductViewEventField)[keyof typeof ProductViewEventField];

export const PRODUCT_VIEW_EVENT_FIELDS = fieldValues(ProductViewEventField);

export const ProductViewEventPhysicalField = {
  CreatedAt: "createdat",
  Customer: "customer",
  CustomerEventId: "_id",
  EventTimestamp: "timestamp",
  EventType: "eventtype",
  IsDuplicate: "is_duplicate",
  ProductId: "product_variant_product_id",
  ProductTitle: "product_variant_product_title",
  SessionId: "sessionid",
  Sku: "product_variant_sku",
  StorefrontProvider: "storefront_provider",
  Team: "team",
  UpdatedAt: "updatedat",
  VariantId: "product_variant_id",
  VariantTitle: "product_variant_title",
} as const;

export type ProductViewEventPhysicalField =
  (typeof ProductViewEventPhysicalField)[keyof typeof ProductViewEventPhysicalField];

export const PRODUCT_VIEW_EVENT_PHYSICAL_FIELDS = fieldValues(
  ProductViewEventPhysicalField,
);

/**
 * The checkout slice of STG_CUSTOMER_EVENTS. That model carries every
 * customer event type, so only the shared identifiers and the checkout total
 * are declared here; the grain narrows to the two checkout event types with
 * a scope filter.
 */
export const StorefrontCheckoutEventField = {
  CheckoutCurrency: "checkout_total_price_currency",
  CheckoutTotalPrice: "checkout_total_price_amount",
  CreatedAt: "createdat",
  Customer: "customer",
  CustomerEventId: "_id",
  EventTimestamp: "timestamp",
  EventType: "eventtype",
  IsDuplicate: "is_duplicate",
  SessionId: "sessionid",
  StorefrontProvider: "storefront_provider",
  Team: "team",
  UpdatedAt: "updatedat",
} as const;

export type StorefrontCheckoutEventField =
  (typeof StorefrontCheckoutEventField)[keyof typeof StorefrontCheckoutEventField];

export const STOREFRONT_CHECKOUT_EVENT_FIELDS = fieldValues(
  StorefrontCheckoutEventField,
);

export const StorefrontCheckoutEventPhysicalField = {
  CheckoutCurrency: "checkout_total_price_currency",
  CheckoutTotalPrice: "checkout_total_price_amount",
  CreatedAt: "createdat",
  Customer: "customer",
  CustomerEventId: "_id",
  EventTimestamp: "timestamp",
  EventType: "eventtype",
  IsDuplicate: "is_duplicate",
  SessionId: "sessionid",
  StorefrontProvider: "storefront_provider",
  Team: "team",
  UpdatedAt: "updatedat",
} as const;

export type StorefrontCheckoutEventPhysicalField =
  (typeof StorefrontCheckoutEventPhysicalField)[keyof typeof StorefrontCheckoutEventPhysicalField];

export const STOREFRONT_CHECKOUT_EVENT_PHYSICAL_FIELDS = fieldValues(
  StorefrontCheckoutEventPhysicalField,
);

/**
 * The cart-line slice of STG_CUSTOMER_EVENTS. That model carries every
 * customer event type, so only the shared identifiers and the cart columns
 * are declared here; the grain narrows to the two cart event types with a
 * scope filter.
 */
export const CartChangeEventField = {
  CartSubtotal: "cart_subtotal_amount",
  CartSubtotalCurrency: "cart_subtotal_currency_code",
  CreatedAt: "createdat",
  Customer: "customer",
  CustomerEventId: "_id",
  EventTimestamp: "timestamp",
  EventType: "eventtype",
  IsDuplicate: "is_duplicate",
  LineQuantity: "cart_line_quantity",
  ProductId: "cart_line_merchandise_product_id",
  ProductTitle: "cart_line_merchandise_product_title",
  SessionId: "sessionid",
  Sku: "cart_line_merchandise_sku",
  StorefrontProvider: "storefront_provider",
  Team: "team",
  UpdatedAt: "updatedat",
  VariantId: "cart_line_merchandise_id",
  VariantTitle: "cart_line_merchandise_title",
} as const;

export type CartChangeEventField =
  (typeof CartChangeEventField)[keyof typeof CartChangeEventField];

export const CART_CHANGE_EVENT_FIELDS = fieldValues(CartChangeEventField);

export const CartChangeEventPhysicalField = {
  CartSubtotal: "cart_subtotal_amount",
  CartSubtotalCurrency: "cart_subtotal_currency_code",
  CreatedAt: "createdat",
  Customer: "customer",
  CustomerEventId: "_id",
  EventTimestamp: "timestamp",
  EventType: "eventtype",
  IsDuplicate: "is_duplicate",
  LineQuantity: "cart_line_quantity",
  ProductId: "cart_line_merchandise_product_id",
  ProductTitle: "cart_line_merchandise_product_title",
  SessionId: "sessionid",
  Sku: "cart_line_merchandise_sku",
  StorefrontProvider: "storefront_provider",
  Team: "team",
  UpdatedAt: "updatedat",
  VariantId: "cart_line_merchandise_id",
  VariantTitle: "cart_line_merchandise_title",
} as const;

export type CartChangeEventPhysicalField =
  (typeof CartChangeEventPhysicalField)[keyof typeof CartChangeEventPhysicalField];

export const CART_CHANGE_EVENT_PHYSICAL_FIELDS = fieldValues(
  CartChangeEventPhysicalField,
);

/**
 * STG_TNPL_CONTRACT_TEMPLATES, every projected column but the loader's
 * sync_to_snowflake_at.
 */
export const TryFirstPlanField = {
  CreatedAt: "created_at",
  LegacyPlanId: "mongo_id",
  ProductIds: "product_ids",
  TeamId: "team_id",
  TryFirstPlanId: "tnpl_contract_template_id",
  UpdatedAt: "updated_at",
} as const;

export type TryFirstPlanField =
  (typeof TryFirstPlanField)[keyof typeof TryFirstPlanField];

export const TRY_FIRST_PLAN_FIELDS = fieldValues(TryFirstPlanField);

export const TryFirstPlanPhysicalField = {
  CreatedAt: "created_at",
  LegacyPlanId: "mongo_id",
  ProductIds: "product_ids",
  TeamId: "team_id",
  TryFirstPlanId: "tnpl_contract_template_id",
  UpdatedAt: "updated_at",
} as const;

export type TryFirstPlanPhysicalField =
  (typeof TryFirstPlanPhysicalField)[keyof typeof TryFirstPlanPhysicalField];

export const TRY_FIRST_PLAN_PHYSICAL_FIELDS = fieldValues(
  TryFirstPlanPhysicalField,
);

/**
 * STG_TNPL_CONTRACTS, minus the loader's sync_to_snowflake_at, minus
 * updated_at, and minus the untyped order_ineligible_reason. The plan
 * reference is physically `tnpl_contract_template`, without the _id suffix
 * the semantic name carries.
 */
export const TryFirstContractField = {
  CancelledReason: "cancelled_reason",
  CreatedAt: "created_at",
  OrderDate: "order_date",
  OrderId: "order_id",
  TeamId: "team_id",
  TryFirstContractId: "tnpl_contract_id",
  TryFirstContractPlanId: "tnpl_contract_template_id",
} as const;

export type TryFirstContractField =
  (typeof TryFirstContractField)[keyof typeof TryFirstContractField];

export const TRY_FIRST_CONTRACT_FIELDS = fieldValues(TryFirstContractField);

export const TryFirstContractPhysicalField = {
  CancelledReason: "cancelled_reason",
  CreatedAt: "created_at",
  OrderDate: "order_date",
  OrderId: "order_id",
  TeamId: "team_id",
  TryFirstContractId: "tnpl_contract_id",
  TryFirstContractPlanId: "tnpl_contract_template",
} as const;

export type TryFirstContractPhysicalField =
  (typeof TryFirstContractPhysicalField)[keyof typeof TryFirstContractPhysicalField];

export const TRY_FIRST_CONTRACT_PHYSICAL_FIELDS = fieldValues(
  TryFirstContractPhysicalField,
);

/**
 * STG_RETURN_LINE_ITEMS_ASSESSMENT_RESPONSES, minus the loader's
 * sync_to_snowflake_at and minus response_completed_at, which the assessment
 * timestamps already cover.
 */
export const ReturnAssessmentResponseField = {
  AssessmentCompletedAt: "assessment_completed_at",
  AssessmentCreatedAt: "assessment_created_at",
  AssessmentDefinitionId: "assessment_definition_id",
  AssessmentId: "assessment_id",
  AssessmentUpdatedAt: "assessment_updated_at",
  LineItemId: "line_item_id",
  OrderId: "order_id",
  OrderLineItemId: "order_line_item_id",
  ResponseId: "response_id",
  ResponseType: "response_type",
  ResponseValue: "response_value",
  ReturnId: "return_id",
  TeamId: "team_id",
} as const;

export type ReturnAssessmentResponseField =
  (typeof ReturnAssessmentResponseField)[keyof typeof ReturnAssessmentResponseField];

export const RETURN_ASSESSMENT_RESPONSE_FIELDS = fieldValues(
  ReturnAssessmentResponseField,
);

export const ReturnAssessmentResponsePhysicalField = {
  AssessmentCompletedAt: "assessment_completed_at",
  AssessmentCreatedAt: "assessment_created_at",
  AssessmentDefinitionId: "assessment_definition_id",
  AssessmentId: "assessment_id",
  AssessmentUpdatedAt: "assessment_updated_at",
  LineItemId: "line_item_id",
  OrderId: "order_id",
  OrderLineItemId: "order_line_item_id",
  ResponseId: "response_id",
  ResponseType: "response_type",
  ResponseValue: "response_value",
  ReturnId: "return_id",
  TeamId: "team_id",
} as const;

export type ReturnAssessmentResponsePhysicalField =
  (typeof ReturnAssessmentResponsePhysicalField)[keyof typeof ReturnAssessmentResponsePhysicalField];

export const RETURN_ASSESSMENT_RESPONSE_PHYSICAL_FIELDS = fieldValues(
  ReturnAssessmentResponsePhysicalField,
);

/**
 * Field rows have no team column. The definition id is the version Mongo id.
 */
export const ReturnAssessmentDefinitionFieldField = {
  CreatedAt: "created_at",
  FieldIndex: "field_index",
  FieldLabel: "field_label",
  FieldRequired: "field_required",
  FieldType: "field_type",
  ReturnAssessmentDefinitionId: "return_assessment_definition_id",
  ReturnAssessmentId: "return_assessment_id",
} as const;

export type ReturnAssessmentDefinitionFieldField =
  (typeof ReturnAssessmentDefinitionFieldField)[keyof typeof ReturnAssessmentDefinitionFieldField];

export const RETURN_ASSESSMENT_DEFINITION_FIELD_FIELDS = fieldValues(
  ReturnAssessmentDefinitionFieldField,
);

export const ReturnAssessmentDefinitionFieldPhysicalField = {
  CreatedAt: "created_at",
  FieldIndex: "field_index",
  FieldLabel: "field_label",
  FieldRequired: "field_required",
  FieldType: "field_type",
  ReturnAssessmentDefinitionId: "return_assessment_definition_mongo_id",
  ReturnAssessmentId: "return_assessment_id",
} as const;

export type ReturnAssessmentDefinitionFieldPhysicalField =
  (typeof ReturnAssessmentDefinitionFieldPhysicalField)[keyof typeof ReturnAssessmentDefinitionFieldPhysicalField];

export const RETURN_ASSESSMENT_DEFINITION_FIELD_PHYSICAL_FIELDS = fieldValues(
  ReturnAssessmentDefinitionFieldPhysicalField,
);

/**
 * STG_RETURN_ASSESSMENTS, every projected column but the loader's
 * sync_to_snowflake_at.
 */
export const ReturnAssessmentField = {
  CreatedAt: "created_at",
  DeletedAt: "deleted_at",
  Kind: "kind",
  MongoId: "mongo_id",
  Name: "name",
  ReturnAssessmentId: "return_assessment_id",
  TeamId: "team_id",
  UpdatedAt: "updated_at",
} as const;

export type ReturnAssessmentField =
  (typeof ReturnAssessmentField)[keyof typeof ReturnAssessmentField];

export const RETURN_ASSESSMENT_FIELDS = fieldValues(ReturnAssessmentField);

export const ReturnAssessmentPhysicalField = {
  CreatedAt: "created_at",
  DeletedAt: "deleted_at",
  Kind: "kind",
  MongoId: "mongo_id",
  Name: "name",
  ReturnAssessmentId: "return_assessment_id",
  TeamId: "team_id",
  UpdatedAt: "updated_at",
} as const;

export type ReturnAssessmentPhysicalField =
  (typeof ReturnAssessmentPhysicalField)[keyof typeof ReturnAssessmentPhysicalField];

export const RETURN_ASSESSMENT_PHYSICAL_FIELDS = fieldValues(
  ReturnAssessmentPhysicalField,
);

/**
 * STG_CUSTOMER_EVENTS_TRACKING_PAGE, every projected column but the
 * loader's sync_to_snowflake_at. The source keeps the raw customer-event
 * names, so the semantic names keep them too.
 */
export const TrackingPageEventField = {
  ClickType: "click_type",
  CreatedAt: "createdat",
  Customer: "customer",
  CustomerEventId: "_id",
  Embedded: "embedded",
  EventTimestamp: "timestamp",
  EventType: "eventtype",
  FulfillmentId: "fulfillment_id",
  Image: "image",
  OrderId: "order_id",
  Team: "team",
  TrackingNumber: "tracking_number",
  TrackingPageId: "tracking_page_id",
  UpdatedAt: "updatedat",
  Url: "url",
} as const;

export type TrackingPageEventField =
  (typeof TrackingPageEventField)[keyof typeof TrackingPageEventField];

export const TRACKING_PAGE_EVENT_FIELDS = fieldValues(TrackingPageEventField);

export const TrackingPageEventPhysicalField = {
  ClickType: "click_type",
  CreatedAt: "createdat",
  Customer: "customer",
  CustomerEventId: "_id",
  Embedded: "embedded",
  EventTimestamp: "timestamp",
  EventType: "eventtype",
  FulfillmentId: "fulfillment_id",
  Image: "image",
  OrderId: "order_id",
  Team: "team",
  TrackingNumber: "tracking_number",
  TrackingPageId: "tracking_page_id",
  UpdatedAt: "updatedat",
  Url: "url",
} as const;

export type TrackingPageEventPhysicalField =
  (typeof TrackingPageEventPhysicalField)[keyof typeof TrackingPageEventPhysicalField];

export const TRACKING_PAGE_EVENT_PHYSICAL_FIELDS = fieldValues(
  TrackingPageEventPhysicalField,
);

/**
 * STG_ORDER_TRACKING_ANALYTICS_PAGES, every projected column but the
 * loader's sync_to_snowflake_at and order_updated_at.
 */
export const OrderTrackingPageOfferClickField = {
  CreatedAt: "created_at",
  EventType: "event_type",
  OrderId: "order_id",
  PageIndex: "page_index",
  TeamId: "team_id",
  Url: "url",
} as const;

export type OrderTrackingPageOfferClickField =
  (typeof OrderTrackingPageOfferClickField)[keyof typeof OrderTrackingPageOfferClickField];

export const ORDER_TRACKING_PAGE_OFFER_CLICK_FIELDS = fieldValues(
  OrderTrackingPageOfferClickField,
);

export const OrderTrackingPageOfferClickPhysicalField = {
  CreatedAt: "created_at",
  EventType: "event_type",
  OrderId: "order_id",
  PageIndex: "page_index",
  TeamId: "team_id",
  Url: "url",
} as const;

export type OrderTrackingPageOfferClickPhysicalField =
  (typeof OrderTrackingPageOfferClickPhysicalField)[keyof typeof OrderTrackingPageOfferClickPhysicalField];

export const ORDER_TRACKING_PAGE_OFFER_CLICK_PHYSICAL_FIELDS = fieldValues(
  OrderTrackingPageOfferClickPhysicalField,
);

/**
 * STG_ORDER_TRACKING_ANALYTICS_EMAILS, restricted to the identifiers, the
 * two type columns and the four moments. The physical names are the source's
 * own: the mail moment is `timestamp`, and delivery, click and open each
 * carry a `_time` column. The recipient list, SMTP response, reporting MTA,
 * processing time, IP addresses, user agents and link tags are deliberately
 * absent, as are sync_to_snowflake_at and order_updated_at.
 */
export const OrderTrackingEmailEventField = {
  ClickedAt: "clicked_at",
  ClickedLink: "clicked_link",
  DeliveredAt: "delivered_at",
  EmailEventIndex: "email_event_index",
  EventType: "event_type",
  MessageId: "message_id",
  OpenedAt: "opened_at",
  OrderId: "order_id",
  SentAt: "sent_at",
  TeamId: "team_id",
  TriggerType: "trigger_type",
} as const;

export type OrderTrackingEmailEventField =
  (typeof OrderTrackingEmailEventField)[keyof typeof OrderTrackingEmailEventField];

export const ORDER_TRACKING_EMAIL_EVENT_FIELDS = fieldValues(
  OrderTrackingEmailEventField,
);

export const OrderTrackingEmailEventPhysicalField = {
  ClickedAt: "click_time",
  ClickedLink: "click_link",
  DeliveredAt: "delivery_time",
  EmailEventIndex: "email_event_index",
  EventType: "event_type",
  MessageId: "message_id",
  OpenedAt: "open_time",
  OrderId: "order_id",
  SentAt: "timestamp",
  TeamId: "team_id",
  TriggerType: "trigger_type",
} as const;

export type OrderTrackingEmailEventPhysicalField =
  (typeof OrderTrackingEmailEventPhysicalField)[keyof typeof OrderTrackingEmailEventPhysicalField];

export const ORDER_TRACKING_EMAIL_EVENT_PHYSICAL_FIELDS = fieldValues(
  OrderTrackingEmailEventPhysicalField,
);

/**
 * STG_ORDER_TRACKING_DETAILS, minus the loader's sync_to_snowflake_at and
 * minus order_updated_at, which moves on any order write and says nothing
 * about the scan.
 */
export const OrderTrackingDetailField = {
  Carrier: "carrier",
  CarrierService: "carrier_service",
  EstDeliveryDate: "est_delivery_date",
  FulfillmentId: "fulfillment_id",
  LocationId: "location_id",
  OrderId: "order_id",
  TeamId: "team_id",
  TrackerCreatedAt: "tracker_created_at",
  TrackerId: "tracker_id",
  TrackerIndex: "tracker_index",
  TrackerStatus: "tracker_status",
  TrackerUpdatedAt: "tracker_updated_at",
  TrackingCode: "tracking_code",
  TrackingDetailIndex: "tracking_detail_index",
  TrackingDetailMessage: "tracking_detail_message",
  TrackingDetailState: "tracking_detail_state",
  TrackingDetailStatus: "tracking_detail_status",
  TrackingDetailTimestamp: "tracking_detail_timestamp",
} as const;

export type OrderTrackingDetailField =
  (typeof OrderTrackingDetailField)[keyof typeof OrderTrackingDetailField];

export const ORDER_TRACKING_DETAIL_FIELDS = fieldValues(
  OrderTrackingDetailField,
);

export const OrderTrackingDetailPhysicalField = {
  Carrier: "carrier",
  CarrierService: "carrier_service",
  EstDeliveryDate: "est_delivery_date",
  FulfillmentId: "fulfillment_id",
  LocationId: "location_id",
  OrderId: "order_id",
  TeamId: "team_id",
  TrackerCreatedAt: "tracker_created_at",
  TrackerId: "tracker_id",
  TrackerIndex: "tracker_index",
  TrackerStatus: "tracker_status",
  TrackerUpdatedAt: "tracker_updated_at",
  TrackingCode: "tracking_code",
  TrackingDetailIndex: "tracking_detail_index",
  TrackingDetailMessage: "tracking_detail_message",
  TrackingDetailState: "tracking_detail_state",
  TrackingDetailStatus: "tracking_detail_status",
  TrackingDetailTimestamp: "tracking_detail_timestamp",
} as const;

export type OrderTrackingDetailPhysicalField =
  (typeof OrderTrackingDetailPhysicalField)[keyof typeof OrderTrackingDetailPhysicalField];

export const ORDER_TRACKING_DETAIL_PHYSICAL_FIELDS = fieldValues(
  OrderTrackingDetailPhysicalField,
);

/**
 * STG_CUSTOMER_EVENTS_INBOUND_RECEIVED, every projected column but the
 * loader's sync_to_snowflake_at. The source keeps the raw event names, so
 * the semantic names rename _id, timestamp, eventtype and the three cost
 * columns to what they mean on an inbound message.
 */
export const InboundMessageField = {
  AutomationId: "automation_id",
  CampaignId: "campaign_id",
  CarrierCostMicros: "carrier_cost_micros",
  Category: "category",
  Channel: "channel",
  CreatedAt: "createdat",
  Customer: "customer",
  EventDate: "event_date",
  EventId: "event_id",
  EventType: "event_type",
  IdempotencyKey: "idempotency_key",
  ImportSource: "import_source",
  IsDuplicate: "is_duplicate",
  MessagePartCount: "message_part_count",
  MessageType: "message_type",
  Team: "team",
  TemplateId: "template_id",
  UpdatedAt: "updatedat",
} as const;

export type InboundMessageField =
  (typeof InboundMessageField)[keyof typeof InboundMessageField];

export const INBOUND_MESSAGE_FIELDS = fieldValues(InboundMessageField);

export const InboundMessagePhysicalField = {
  AutomationId: "automation",
  CampaignId: "campaign",
  CarrierCostMicros: "costcarrierfeemicros",
  Category: "category",
  Channel: "channel",
  CreatedAt: "createdat",
  Customer: "customer",
  EventDate: "timestamp",
  EventId: "_id",
  EventType: "eventtype",
  IdempotencyKey: "idempotency_key",
  ImportSource: "import_source",
  IsDuplicate: "is_duplicate",
  MessagePartCount: "costcount",
  MessageType: "costmessagetype",
  Team: "team",
  TemplateId: "template_id",
  UpdatedAt: "updatedat",
} as const;

export type InboundMessagePhysicalField =
  (typeof InboundMessagePhysicalField)[keyof typeof InboundMessagePhysicalField];

export const INBOUND_MESSAGE_PHYSICAL_FIELDS = fieldValues(
  InboundMessagePhysicalField,
);

/**
 * STG_ORDER_MERCURY_SHIPPING_LINES, every projected column but the loader's
 * sync_to_snowflake_at. The money columns carry the source's shop_money
 * naming physically and drop it in the semantic name.
 */
export const MercuryShippingLineField = {
  CheckoutExperienceId: "checkout_experience_id",
  CheckoutExperienceNodeId: "checkout_experience_node_id",
  DiscountedPriceAmount: "discounted_price_amount",
  DiscountedPriceCurrency: "discounted_price_currency",
  LabelCostAmount: "label_cost_amount",
  LabelCostCurrency: "label_cost_currency",
  OrderCreatedAt: "order_created_at",
  OrderId: "order_id",
  OrderUpdatedAt: "order_updated_at",
  RateTableId: "rate_table_id",
  ShippingLineId: "shipping_line_id",
  ShippingLineIndex: "shipping_line_index",
  TeamId: "team_id",
} as const;

export type MercuryShippingLineField =
  (typeof MercuryShippingLineField)[keyof typeof MercuryShippingLineField];

export const MERCURY_SHIPPING_LINE_FIELDS = fieldValues(
  MercuryShippingLineField,
);

export const MercuryShippingLinePhysicalField = {
  CheckoutExperienceId: "checkout_experience_id",
  CheckoutExperienceNodeId: "checkout_experience_node_id",
  DiscountedPriceAmount: "discounted_price_shop_money_amount",
  DiscountedPriceCurrency: "discounted_price_shop_money_currency",
  LabelCostAmount: "label_cost_shop_money_amount",
  LabelCostCurrency: "label_cost_shop_money_currency",
  OrderCreatedAt: "order_created_at",
  OrderId: "order_id",
  OrderUpdatedAt: "order_updated_at",
  RateTableId: "rate_table_id",
  ShippingLineId: "shipping_line_id",
  ShippingLineIndex: "shipping_line_index",
  TeamId: "team_id",
} as const;

export type MercuryShippingLinePhysicalField =
  (typeof MercuryShippingLinePhysicalField)[keyof typeof MercuryShippingLinePhysicalField];

export const MERCURY_SHIPPING_LINE_PHYSICAL_FIELDS = fieldValues(
  MercuryShippingLinePhysicalField,
);

/**
 * STG_ORDER_PAYMENT_GATEWAY_NAMES names four columns beside the loader's
 * sync_to_snowflake_at; this grain declares all four.
 */
export const OrderPaymentGatewayNameField = {
  GatewayNameIndex: "gateway_name_index",
  OrderId: "order_id",
  PaymentGatewayName: "payment_gateway_name",
  TeamId: "team_id",
} as const;

export type OrderPaymentGatewayNameField =
  (typeof OrderPaymentGatewayNameField)[keyof typeof OrderPaymentGatewayNameField];

export const ORDER_PAYMENT_GATEWAY_NAME_FIELDS = fieldValues(
  OrderPaymentGatewayNameField,
);

export const OrderPaymentGatewayNamePhysicalField = {
  GatewayNameIndex: "gateway_name_index",
  OrderId: "order_id",
  PaymentGatewayName: "payment_gateway_name",
  TeamId: "team_id",
} as const;

export type OrderPaymentGatewayNamePhysicalField =
  (typeof OrderPaymentGatewayNamePhysicalField)[keyof typeof OrderPaymentGatewayNamePhysicalField];

export const ORDER_PAYMENT_GATEWAY_NAME_PHYSICAL_FIELDS = fieldValues(
  OrderPaymentGatewayNamePhysicalField,
);

/**
 * STG_INTEGRATIONS, minus every credential and configuration blob it also
 * projects: the admin API access token and scopes, the carrier accounts, the
 * shipping coverage and sales channel configuration, the plan block, the
 * backfill watermark, and the merchant contact name and email.
 */
export const IntegrationField = {
  CreatedAt: "created_at",
  Enabled: "enabled",
  IntegrationId: "integration_id",
  Kind: "kind",
  ShopifyDomain: "shopify_domain",
  TeamId: "team_id",
  UpdatedAt: "updated_at",
} as const;
export type IntegrationField =
  (typeof IntegrationField)[keyof typeof IntegrationField];
export const INTEGRATION_FIELDS = fieldValues(IntegrationField);
export const IntegrationPhysicalField = {
  CreatedAt: "createdat",
  Enabled: "enabled",
  IntegrationId: "integration_id",
  Kind: "kind",
  ShopifyDomain: "shopify_domain",
  TeamId: "team_id",
  UpdatedAt: "updatedat",
} as const;
export type IntegrationPhysicalField =
  (typeof IntegrationPhysicalField)[keyof typeof IntegrationPhysicalField];
export const INTEGRATION_PHYSICAL_FIELDS = fieldValues(
  IntegrationPhysicalField,
);

/**
 * STG_CUSTOMERS, minus the geography point, the pipeline watermark, the update
 * timestamp, the split name parts, the anonymous id and the duplicate encodings.
 */
export const CustomerField = {
  Birthday: "birthday",
  CanReceiveEmailMarketing: "can_receive_email_marketing",
  CreatedAt: "created_at",
  Email: "email",
  EmailSubscribed: "email_subscribed",
  Id: "_id",
  LocationCity: "location_city",
  LocationCountryCode: "location_country_code",
  LocationPostalCode: "location_postal_code",
  LocationState: "location_state",
  LoyaltyLionEnrolled: "loyalty_lion_enrolled",
  LoyaltyLionTierId: "loyalty_lion_tier_id",
  Name: "name",
  PhoneNumber: "phone_number",
  ShopifyId: "shopify_id",
  SmsSubscribed: "sms_subscribed",
  Team: "team",
} as const;

export type CustomerField = (typeof CustomerField)[keyof typeof CustomerField];

export const CUSTOMER_FIELDS = fieldValues(CustomerField);

/**
 * The two subscription columns are physically BOOLEAN despite their status
 * names, so the semantic names above say subscribed and these keep the column.
 */
export const CustomerPhysicalField = {
  Birthday: "birthday",
  CanReceiveEmailMarketing: "canreceiveemailmarketing",
  CreatedAt: "createdat",
  Email: "email",
  EmailSubscribed: "emailsubscriptionstatus",
  Id: "_id",
  LocationCity: "location_city",
  LocationCountryCode: "location_country_code",
  LocationPostalCode: "location_postal_code",
  LocationState: "location_state",
  LoyaltyLionEnrolled: "loyalty_lion_enrolled",
  LoyaltyLionTierId: "loyalty_lion_tier_id",
  Name: "name",
  PhoneNumber: "phone_number",
  ShopifyId: "shopify_id",
  SmsSubscribed: "smssubscriptionstatus",
  Team: "team",
} as const;

export type CustomerPhysicalField =
  (typeof CustomerPhysicalField)[keyof typeof CustomerPhysicalField];

export const CUSTOMER_PHYSICAL_FIELDS = fieldValues(CustomerPhysicalField);

export const SupportTicketField = {
  AccountId: "account_id",
  AssigneeType: "assignee_type",
  ClosureReason: "closure_reason",
  CreatedAt: "created_at",
  DeletedAt: "deleted_at",
  LastCustomerMessageAt: "last_customer_message_at",
  LastMerchantMessageAt: "last_merchant_message_at",
  Priority: "priority",
  Status: "status",
  StatusUpdatedAt: "status_updated_at",
  TicketNumber: "ticket_number",
  TopicId: "topic_id",
} as const;

export type SupportTicketField =
  (typeof SupportTicketField)[keyof typeof SupportTicketField];

export const SUPPORT_TICKET_FIELDS = fieldValues(SupportTicketField);

export const SupportTicketPhysicalField = {
  AccountId: "account_id",
  AssigneeType: "assignee_type",
  ClosureReason: "closure_reason",
  CreatedAt: "created_at",
  DeletedAt: "deleted_at",
  LastCustomerMessageAt: "last_customer_message_at",
  LastMerchantMessageAt: "last_merchant_message_at",
  Priority: "priority",
  Status: "status",
  StatusUpdatedAt: "status_updated_at",
  TicketNumber: "ticket_number",
  TopicId: "topic_id",
} as const;

export type SupportTicketPhysicalField =
  (typeof SupportTicketPhysicalField)[keyof typeof SupportTicketPhysicalField];

export const SUPPORT_TICKET_PHYSICAL_FIELDS = fieldValues(
  SupportTicketPhysicalField,
);

export const SupportTicketMessageField = {
  AccountId: "account_id",
  Channel: "channel",
  DeletedAt: "deleted_at",
  MessageId: "message_id",
  SenderType: "sender_type",
  SentAt: "sent_at",
} as const;

export type SupportTicketMessageField =
  (typeof SupportTicketMessageField)[keyof typeof SupportTicketMessageField];

export const SUPPORT_TICKET_MESSAGE_FIELDS = fieldValues(
  SupportTicketMessageField,
);

export const SupportTicketMessagePhysicalField = {
  AccountId: "account_id",
  Channel: "channel",
  DeletedAt: "deleted_at",
  MessageId: "message_id",
  SenderType: "sender_type",
  SentAt: "sent_at",
} as const;

export type SupportTicketMessagePhysicalField =
  (typeof SupportTicketMessagePhysicalField)[keyof typeof SupportTicketMessagePhysicalField];

export const SUPPORT_TICKET_MESSAGE_PHYSICAL_FIELDS = fieldValues(
  SupportTicketMessagePhysicalField,
);

export const MessageTopicLinkField = {
  AccountId: "account_id",
  MessageId: "message_id",
  MessageSentAt: "message_sent_at",
  MessageTopicLinkId: "message_topic_link_id",
  TopicId: "topic_id",
  TopicKind: "topic_kind",
} as const;

export type MessageTopicLinkField =
  (typeof MessageTopicLinkField)[keyof typeof MessageTopicLinkField];

export const MESSAGE_TOPIC_LINK_FIELDS = fieldValues(MessageTopicLinkField);

export const MessageTopicLinkPhysicalField = {
  AccountId: "account_id",
  MessageId: "message_id",
  MessageSentAt: "message_sent_at",
  MessageTopicLinkId: "message_topic_link_id",
  TopicId: "topic_id",
  TopicKind: "topic_kind",
} as const;

export type MessageTopicLinkPhysicalField =
  (typeof MessageTopicLinkPhysicalField)[keyof typeof MessageTopicLinkPhysicalField];

export const MESSAGE_TOPIC_LINK_PHYSICAL_FIELDS = fieldValues(
  MessageTopicLinkPhysicalField,
);

export const SupportTicketTopicField = {
  AccountId: "account_id",
  CreatedAt: "created_at",
  DeletedAt: "deleted_at",
  Kind: "kind",
  TopicId: "topic_id",
} as const;

export type SupportTicketTopicField =
  (typeof SupportTicketTopicField)[keyof typeof SupportTicketTopicField];

export const SUPPORT_TICKET_TOPIC_FIELDS = fieldValues(SupportTicketTopicField);

export const SupportTicketTopicPhysicalField = {
  AccountId: "account_id",
  CreatedAt: "created_at",
  DeletedAt: "deleted_at",
  Kind: "kind",
  TopicId: "topic_id",
} as const;

export type SupportTicketTopicPhysicalField =
  (typeof SupportTicketTopicPhysicalField)[keyof typeof SupportTicketTopicPhysicalField];

export const SUPPORT_TICKET_TOPIC_PHYSICAL_FIELDS = fieldValues(
  SupportTicketTopicPhysicalField,
);

export interface SemanticFieldByTable {
  readonly [SemanticTable.CheckoutWidgets]: CheckoutWidgetField;
  readonly [SemanticTable.CheckoutWidgetAttributedOrderLineItems]: CheckoutWidgetAttributedOrderLineItemsField;
  readonly [SemanticTable.OrderLineItemProperties]: OrderLineItemPropertyField;
  readonly [SemanticTable.FulfillmentGroups]: FulfillmentGroupField;
  readonly [SemanticTable.FulfillmentGroupLineItems]: FulfillmentGroupLineItemField;
  readonly [SemanticTable.FulfillmentGroupShipments]: FulfillmentGroupShipmentField;
  readonly [SemanticTable.FulfillmentGroupShipmentSurcharges]: FulfillmentGroupShipmentSurchargeField;
  readonly [SemanticTable.FulfillmentGroupFulfillmentOrders]: FulfillmentGroupFulfillmentOrderField;
  readonly [SemanticTable.Orders]: OrderField;
  readonly [SemanticTable.OrderLineItems]: OrderLineItemField;
  readonly [SemanticTable.ExchangeOrders]: ExchangeOrderField;
  readonly [SemanticTable.ReturnLineItems]: ReturnLineItemField;
  readonly [SemanticTable.OrderFulfillments]: OrderFulfillmentField;
  readonly [SemanticTable.OrderFulfillmentLineItems]: OrderFulfillmentLineItemField;
  readonly [SemanticTable.ReturnShipmentTrackingDetails]: ReturnShipmentTrackingDetailField;
  readonly [SemanticTable.ReturnLineItemMultipleChoice]: ReturnLineItemMultipleChoiceField;
  readonly [SemanticTable.Returns]: ReturnField;
  readonly [SemanticTable.ReturnPaymentIntents]: ReturnPaymentIntentField;
  readonly [SemanticTable.ReturnShipments]: ReturnShipmentField;
  readonly [SemanticTable.MarketingOutreachEvents]: MarketingOutreachEventField;
  readonly [SemanticTable.Campaigns]: CampaignField;
  readonly [SemanticTable.Automations]: AutomationField;
  readonly [SemanticTable.CheckoutVisits]: CheckoutVisitField;
  readonly [SemanticTable.CheckoutVisitAdvanced]: CheckoutVisitAdvancedField;
  readonly [SemanticTable.CheckoutVisitTreatments]: CheckoutVisitTreatmentField;
  readonly [SemanticTable.Treatments]: TreatmentField;
  readonly [SemanticTable.Splits]: SplitField;
  readonly [SemanticTable.OrderShippingLines]: OrderShippingLineField;
  readonly [SemanticTable.OrderRefundLineItems]: OrderRefundLineItemField;
  readonly [SemanticTable.PostPurchaseUpsellEvents]: PostPurchaseUpsellEventField;
  readonly [SemanticTable.PostPurchaseUpsellPages]: PostPurchaseUpsellPageField;
  readonly [SemanticTable.Products]: ProductField;
  readonly [SemanticTable.InventoryLevels]: InventoryLevelField;
  readonly [SemanticTable.StorageLocations]: StorageLocationField;
  readonly [SemanticTable.Locations]: LocationField;
  readonly [SemanticTable.Bills]: BillField;
  readonly [SemanticTable.BillingCharges]: BillingChargeField;
  readonly [SemanticTable.RescuePanelMetrics]: RescuePanelMetricsField;
  readonly [SemanticTable.RescuePanelAttributedOrders]: RescuePanelAttributedOrdersField;
  readonly [SemanticTable.ShippingClaims]: ShippingClaimField;
  readonly [SemanticTable.DraftReturnPendingReturnItems]: DraftReturnPendingReturnItemField;
  readonly [SemanticTable.Conversations]: ConversationField;
  readonly [SemanticTable.ConversationMessages]: ConversationMessageField;
  readonly [SemanticTable.ReturnTags]: ReturnTagField;
  readonly [SemanticTable.SatisfactionResponses]: SatisfactionResponseField;
  readonly [SemanticTable.OrderExtendedWarranties]: OrderExtendedWarrantyField;
  readonly [SemanticTable.FailedDiscountRescueEvents]: FailedDiscountRescueEventField;
  readonly [SemanticTable.CustomerTags]: CustomerTagField;
  readonly [SemanticTable.CustomCustomerEvents]: CustomCustomerEventField;
  readonly [SemanticTable.CustomerCustomFields]: CustomerCustomFieldField;
  readonly [SemanticTable.ProductTags]: ProductTagField;
  readonly [SemanticTable.FulfillmentGroupTrackingEvents]: FulfillmentGroupTrackingEventField;
  readonly [SemanticTable.ProductCollections]: ProductCollectionField;
  readonly [SemanticTable.ProductReviews]: ProductReviewField;
  readonly [SemanticTable.DailyShopperSessions]: DailyShopperSessionField;
  readonly [SemanticTable.MarketingAgentDecisions]: MarketingAgentDecisionField;
  readonly [SemanticTable.MarketingAgentDecisionTags]: MarketingAgentDecisionTagField;
  readonly [SemanticTable.CustomerGroupMemberships]: CustomerGroupMembershipField;
  readonly [SemanticTable.CampaignRecipients]: CampaignRecipientField;
  readonly [SemanticTable.CheckoutDeliveryOptions]: CheckoutDeliveryOptionField;
  readonly [SemanticTable.CheckoutDeliveryGroups]: CheckoutDeliveryGroupField;
  readonly [SemanticTable.CheckoutInfoChanges]: CheckoutInfoChangeField;
  readonly [SemanticTable.CheckoutShippingStepEvents]: CheckoutShippingStepEventField;
  readonly [SemanticTable.CheckoutOrderCreatedEvents]: CheckoutOrderCreatedEventField;
  readonly [SemanticTable.CheckoutWidgetInteractions]: CheckoutWidgetInteractionField;
  readonly [SemanticTable.PostPurchaseSplitAssignments]: PostPurchaseSplitAssignmentField;
  readonly [SemanticTable.MarketingSubscriptionEvents]: MarketingSubscriptionEventField;
  readonly [SemanticTable.BackInStockEvents]: BackInStockEventField;
  readonly [SemanticTable.CollectionViewEvents]: CollectionViewEventField;
  readonly [SemanticTable.ProductViewEvents]: ProductViewEventField;
  readonly [SemanticTable.StorefrontCheckoutEvents]: StorefrontCheckoutEventField;
  readonly [SemanticTable.CartChangeEvents]: CartChangeEventField;
  readonly [SemanticTable.TryFirstPlans]: TryFirstPlanField;
  readonly [SemanticTable.TryFirstContracts]: TryFirstContractField;
  readonly [SemanticTable.ReturnAssessmentResponses]: ReturnAssessmentResponseField;
  readonly [SemanticTable.ReturnAssessmentDefinitionFields]: ReturnAssessmentDefinitionFieldField;
  readonly [SemanticTable.ReturnAssessments]: ReturnAssessmentField;
  readonly [SemanticTable.TrackingPageEvents]: TrackingPageEventField;
  readonly [SemanticTable.OrderTrackingPageOfferClicks]: OrderTrackingPageOfferClickField;
  readonly [SemanticTable.OrderTrackingEmailEvents]: OrderTrackingEmailEventField;
  readonly [SemanticTable.OrderTrackingDetails]: OrderTrackingDetailField;
  readonly [SemanticTable.InboundMessages]: InboundMessageField;
  readonly [SemanticTable.MercuryShippingLines]: MercuryShippingLineField;
  readonly [SemanticTable.OrderPaymentGatewayNames]: OrderPaymentGatewayNameField;
  readonly [SemanticTable.Integrations]: IntegrationField;
  readonly [SemanticTable.Customers]: CustomerField;
  readonly [SemanticTable.SupportTickets]: SupportTicketField;
  readonly [SemanticTable.SupportTicketMessages]: SupportTicketMessageField;
  readonly [SemanticTable.MessageTopicLinks]: MessageTopicLinkField;
  readonly [SemanticTable.SupportTicketTopics]: SupportTicketTopicField;
}

export type SemanticFieldName<TTable extends SemanticTableName> =
  SemanticFieldByTable[TTable];

export type AnySemanticFieldName =
  SemanticFieldByTable[keyof SemanticFieldByTable];

export interface SemanticPhysicalFieldByTable {
  readonly [SemanticTable.CheckoutWidgets]: CheckoutWidgetPhysicalField;
  readonly [SemanticTable.CheckoutWidgetAttributedOrderLineItems]: CheckoutWidgetAttributedOrderLineItemsPhysicalField;
  readonly [SemanticTable.OrderLineItemProperties]: OrderLineItemPropertyPhysicalField;
  readonly [SemanticTable.FulfillmentGroups]: FulfillmentGroupPhysicalField;
  readonly [SemanticTable.FulfillmentGroupLineItems]: FulfillmentGroupLineItemPhysicalField;
  readonly [SemanticTable.FulfillmentGroupShipments]: FulfillmentGroupShipmentPhysicalField;
  readonly [SemanticTable.FulfillmentGroupShipmentSurcharges]: FulfillmentGroupShipmentSurchargePhysicalField;
  readonly [SemanticTable.FulfillmentGroupFulfillmentOrders]: FulfillmentGroupFulfillmentOrderPhysicalField;
  readonly [SemanticTable.Orders]: OrderPhysicalField;
  readonly [SemanticTable.OrderLineItems]: OrderLineItemPhysicalField;
  readonly [SemanticTable.ExchangeOrders]: ExchangeOrderPhysicalField;
  readonly [SemanticTable.ReturnLineItems]: ReturnLineItemPhysicalField;
  readonly [SemanticTable.OrderFulfillments]: OrderFulfillmentPhysicalField;
  readonly [SemanticTable.OrderFulfillmentLineItems]: OrderFulfillmentLineItemPhysicalField;
  readonly [SemanticTable.ReturnShipmentTrackingDetails]: ReturnShipmentTrackingDetailPhysicalField;
  readonly [SemanticTable.ReturnLineItemMultipleChoice]: ReturnLineItemMultipleChoicePhysicalField;
  readonly [SemanticTable.Returns]: ReturnPhysicalField;
  readonly [SemanticTable.ReturnPaymentIntents]: ReturnPaymentIntentPhysicalField;
  readonly [SemanticTable.ReturnShipments]: ReturnShipmentPhysicalField;
  readonly [SemanticTable.MarketingOutreachEvents]: MarketingOutreachEventPhysicalField;
  readonly [SemanticTable.Campaigns]: CampaignPhysicalField;
  readonly [SemanticTable.Automations]: AutomationPhysicalField;
  readonly [SemanticTable.CheckoutVisits]: CheckoutVisitPhysicalField;
  readonly [SemanticTable.CheckoutVisitAdvanced]: CheckoutVisitAdvancedPhysicalField;
  readonly [SemanticTable.CheckoutVisitTreatments]: CheckoutVisitTreatmentPhysicalField;
  readonly [SemanticTable.Treatments]: TreatmentPhysicalField;
  readonly [SemanticTable.Splits]: SplitPhysicalField;
  readonly [SemanticTable.OrderShippingLines]: OrderShippingLinePhysicalField;
  readonly [SemanticTable.OrderRefundLineItems]: OrderRefundLineItemPhysicalField;
  readonly [SemanticTable.PostPurchaseUpsellEvents]: PostPurchaseUpsellEventPhysicalField;
  readonly [SemanticTable.PostPurchaseUpsellPages]: PostPurchaseUpsellPagePhysicalField;
  readonly [SemanticTable.Products]: ProductPhysicalField;
  readonly [SemanticTable.InventoryLevels]: InventoryLevelPhysicalField;
  readonly [SemanticTable.StorageLocations]: StorageLocationPhysicalField;
  readonly [SemanticTable.Locations]: LocationPhysicalField;
  readonly [SemanticTable.Bills]: BillPhysicalField;
  readonly [SemanticTable.BillingCharges]: BillingChargePhysicalField;
  readonly [SemanticTable.RescuePanelMetrics]: RescuePanelMetricsPhysicalField;
  readonly [SemanticTable.RescuePanelAttributedOrders]: RescuePanelAttributedOrdersPhysicalField;
  readonly [SemanticTable.ShippingClaims]: ShippingClaimPhysicalField;
  readonly [SemanticTable.DraftReturnPendingReturnItems]: DraftReturnPendingReturnItemPhysicalField;
  readonly [SemanticTable.Conversations]: ConversationPhysicalField;
  readonly [SemanticTable.ConversationMessages]: ConversationMessagePhysicalField;
  readonly [SemanticTable.ReturnTags]: ReturnTagPhysicalField;
  readonly [SemanticTable.SatisfactionResponses]: SatisfactionResponsePhysicalField;
  readonly [SemanticTable.OrderExtendedWarranties]: OrderExtendedWarrantyPhysicalField;
  readonly [SemanticTable.FailedDiscountRescueEvents]: FailedDiscountRescueEventPhysicalField;
  readonly [SemanticTable.CustomerTags]: CustomerTagPhysicalField;
  readonly [SemanticTable.CustomCustomerEvents]: CustomCustomerEventPhysicalField;
  readonly [SemanticTable.CustomerCustomFields]: CustomerCustomFieldPhysicalField;
  readonly [SemanticTable.ProductTags]: ProductTagPhysicalField;
  readonly [SemanticTable.FulfillmentGroupTrackingEvents]: FulfillmentGroupTrackingEventPhysicalField;
  readonly [SemanticTable.ProductCollections]: ProductCollectionPhysicalField;
  readonly [SemanticTable.ProductReviews]: ProductReviewPhysicalField;
  readonly [SemanticTable.DailyShopperSessions]: DailyShopperSessionPhysicalField;
  readonly [SemanticTable.MarketingAgentDecisions]: MarketingAgentDecisionPhysicalField;
  readonly [SemanticTable.MarketingAgentDecisionTags]: MarketingAgentDecisionTagPhysicalField;
  readonly [SemanticTable.CustomerGroupMemberships]: CustomerGroupMembershipPhysicalField;
  readonly [SemanticTable.CampaignRecipients]: CampaignRecipientPhysicalField;
  readonly [SemanticTable.CheckoutDeliveryOptions]: CheckoutDeliveryOptionPhysicalField;
  readonly [SemanticTable.CheckoutDeliveryGroups]: CheckoutDeliveryGroupPhysicalField;
  readonly [SemanticTable.CheckoutInfoChanges]: CheckoutInfoChangePhysicalField;
  readonly [SemanticTable.CheckoutShippingStepEvents]: CheckoutShippingStepEventPhysicalField;
  readonly [SemanticTable.CheckoutOrderCreatedEvents]: CheckoutOrderCreatedEventPhysicalField;
  readonly [SemanticTable.CheckoutWidgetInteractions]: CheckoutWidgetInteractionPhysicalField;
  readonly [SemanticTable.PostPurchaseSplitAssignments]: PostPurchaseSplitAssignmentPhysicalField;
  readonly [SemanticTable.MarketingSubscriptionEvents]: MarketingSubscriptionEventPhysicalField;
  readonly [SemanticTable.BackInStockEvents]: BackInStockEventPhysicalField;
  readonly [SemanticTable.CollectionViewEvents]: CollectionViewEventPhysicalField;
  readonly [SemanticTable.ProductViewEvents]: ProductViewEventPhysicalField;
  readonly [SemanticTable.StorefrontCheckoutEvents]: StorefrontCheckoutEventPhysicalField;
  readonly [SemanticTable.CartChangeEvents]: CartChangeEventPhysicalField;
  readonly [SemanticTable.TryFirstPlans]: TryFirstPlanPhysicalField;
  readonly [SemanticTable.TryFirstContracts]: TryFirstContractPhysicalField;
  readonly [SemanticTable.ReturnAssessmentResponses]: ReturnAssessmentResponsePhysicalField;
  readonly [SemanticTable.ReturnAssessmentDefinitionFields]: ReturnAssessmentDefinitionFieldPhysicalField;
  readonly [SemanticTable.ReturnAssessments]: ReturnAssessmentPhysicalField;
  readonly [SemanticTable.TrackingPageEvents]: TrackingPageEventPhysicalField;
  readonly [SemanticTable.OrderTrackingPageOfferClicks]: OrderTrackingPageOfferClickPhysicalField;
  readonly [SemanticTable.OrderTrackingEmailEvents]: OrderTrackingEmailEventPhysicalField;
  readonly [SemanticTable.OrderTrackingDetails]: OrderTrackingDetailPhysicalField;
  readonly [SemanticTable.InboundMessages]: InboundMessagePhysicalField;
  readonly [SemanticTable.MercuryShippingLines]: MercuryShippingLinePhysicalField;
  readonly [SemanticTable.OrderPaymentGatewayNames]: OrderPaymentGatewayNamePhysicalField;
  readonly [SemanticTable.Integrations]: IntegrationPhysicalField;
  readonly [SemanticTable.Customers]: CustomerPhysicalField;
  readonly [SemanticTable.SupportTickets]: SupportTicketPhysicalField;
  readonly [SemanticTable.SupportTicketMessages]: SupportTicketMessagePhysicalField;
  readonly [SemanticTable.MessageTopicLinks]: MessageTopicLinkPhysicalField;
  readonly [SemanticTable.SupportTicketTopics]: SupportTicketTopicPhysicalField;
}

export type SemanticPhysicalFieldName<TTable extends SemanticTableName> =
  SemanticPhysicalFieldByTable[TTable];

export type AnySemanticPhysicalFieldName =
  SemanticPhysicalFieldByTable[keyof SemanticPhysicalFieldByTable];

/** Which join site of a semantic table a ref means; absent is the default site. */
export type SemanticTableOccurrence = string;

export interface SemanticFieldRef<TTable extends SemanticTableName> {
  readonly table: TTable;
  readonly occurrence?: SemanticTableOccurrence;
  readonly field: SemanticFieldName<TTable>;
}

export type AnySemanticFieldRef = {
  readonly [TTable in SemanticTableName]: SemanticFieldRef<TTable>;
}[SemanticTableName];

export const CheckoutVisitField = {
  CustomerEventId: "customer_event_id",
  TeamId: "team_id",
  VisitSource: "visit_source",
  VisitTimestampUtc: "visit_timestamp_utc",
  LocalTimestamp: "local_timestamp",
  LocalDayOfWeek: "local_day_of_week",
  LocalTimeInSeconds: "local_time_in_seconds",
  OrderId: "order_id",
  OrderValue: "order_value",
  Currency: "currency",
  ShippingCountryCode: "shipping_country_code",
  ShippingProvinceCode: "shipping_province_code",
} as const;

export type CheckoutVisitField =
  (typeof CheckoutVisitField)[keyof typeof CheckoutVisitField];

export const CHECKOUT_VISIT_FIELDS = fieldValues(CheckoutVisitField);

export const CheckoutVisitPhysicalField = {
  CustomerEventId: "customer_event_id",
  TeamId: "team_id",
  VisitSource: "visit_source",
  VisitTimestampUtc: "visit_timestamp_utc",
  LocalTimestamp: "local_timestamp",
  LocalDayOfWeek: "local_day_of_week",
  LocalTimeInSeconds: "local_time_in_seconds",
  OrderId: "order_id",
  OrderValue: "order_value",
  Currency: "currency",
  ShippingCountryCode: "shipping_country_code",
  ShippingProvinceCode: "shipping_province_code",
} as const;

export type CheckoutVisitPhysicalField =
  (typeof CheckoutVisitPhysicalField)[keyof typeof CheckoutVisitPhysicalField];

export const CHECKOUT_VISIT_PHYSICAL_FIELDS = fieldValues(
  CheckoutVisitPhysicalField,
);

export const CheckoutVisitAdvancedField = {
  CustomerEventId: "customer_event_id",
  TeamId: "team_id",
  ItemQuantity: "item_quantity",
  CustomerOrderCountValue: "customer_order_count_value",
  BrowseCountryCode: "browse_country_code",
  BrowseProvinceCode: "browse_province_code",
  CheckoutToken: "checkout_token",
} as const;

export type CheckoutVisitAdvancedField =
  (typeof CheckoutVisitAdvancedField)[keyof typeof CheckoutVisitAdvancedField];

export const CHECKOUT_VISIT_ADVANCED_FIELDS = fieldValues(
  CheckoutVisitAdvancedField,
);

export const CheckoutVisitAdvancedPhysicalField = {
  CustomerEventId: "customer_event_id",
  TeamId: "team_id",
  ItemQuantity: "item_quantity",
  CustomerOrderCountValue: "customer_order_count_value",
  BrowseCountryCode: "browse_country_code",
  BrowseProvinceCode: "browse_province_code",
  CheckoutToken: "checkout_token",
} as const;

export type CheckoutVisitAdvancedPhysicalField =
  (typeof CheckoutVisitAdvancedPhysicalField)[keyof typeof CheckoutVisitAdvancedPhysicalField];

export const CHECKOUT_VISIT_ADVANCED_PHYSICAL_FIELDS = fieldValues(
  CheckoutVisitAdvancedPhysicalField,
);

export const CheckoutVisitTreatmentField = {
  CustomerEventId: "customer_event_id",
  TeamId: "team_id",
  TreatmentId: "treatment_id",
} as const;

export type CheckoutVisitTreatmentField =
  (typeof CheckoutVisitTreatmentField)[keyof typeof CheckoutVisitTreatmentField];

export const CHECKOUT_VISIT_TREATMENT_FIELDS = fieldValues(
  CheckoutVisitTreatmentField,
);

export const CheckoutVisitTreatmentPhysicalField = {
  CustomerEventId: "customer_event_id",
  TeamId: "team_id",
  TreatmentId: "treatment_id",
} as const;

export type CheckoutVisitTreatmentPhysicalField =
  (typeof CheckoutVisitTreatmentPhysicalField)[keyof typeof CheckoutVisitTreatmentPhysicalField];

export const CHECKOUT_VISIT_TREATMENT_PHYSICAL_FIELDS = fieldValues(
  CheckoutVisitTreatmentPhysicalField,
);

export const TreatmentField = {
  Id: "id",
  TeamId: "team_id",
  SplitId: "split_id",
  SourceType: "source_type",
  Name: "name",
} as const;

export type TreatmentField =
  (typeof TreatmentField)[keyof typeof TreatmentField];

export const TREATMENT_FIELDS = fieldValues(TreatmentField);

export const TreatmentPhysicalField = {
  Id: "id",
  TeamId: "team_id",
  SplitId: "split_id",
  SourceType: "source_type",
  Name: "name",
} as const;

export type TreatmentPhysicalField =
  (typeof TreatmentPhysicalField)[keyof typeof TreatmentPhysicalField];

export const TREATMENT_PHYSICAL_FIELDS = fieldValues(TreatmentPhysicalField);

export const SplitField = {
  SplitId: "split_id",
  TeamId: "team_id",
  Name: "name",
  TreatmentType: "treatmenttype",
  ControlTreatmentId: "control_treatment_id",
  Active: "active",
  Deleted: "deleted",
  StartDate: "startdate",
  EndDate: "enddate",
} as const;

export type SplitField = (typeof SplitField)[keyof typeof SplitField];

export const SPLIT_FIELDS = fieldValues(SplitField);

export const SplitPhysicalField = {
  SplitId: "split_id",
  TeamId: "team_id",
  Name: "name",
  TreatmentType: "treatmenttype",
  ControlTreatmentId: "control_treatment_id",
  Active: "active",
  Deleted: "deleted",
  StartDate: "startdate",
  EndDate: "enddate",
} as const;

export type SplitPhysicalField =
  (typeof SplitPhysicalField)[keyof typeof SplitPhysicalField];

export const SPLIT_PHYSICAL_FIELDS = fieldValues(SplitPhysicalField);

export const OrderShippingLineField = {
  ShippingLineId: "shipping_line_id",
  ShippingLineIndex: "shipping_line_index",
  OrderId: "order_id",
  TeamId: "team_id",
  PriceSetShopMoneyAmount: "price_set_shop_money_amount",
  PriceSetShopMoneyCurrencyCode: "price_set_shop_money_currency_code",
  DiscountedPriceSetShopMoneyAmount: "discounted_price_set_shop_money_amount",
  Title: "title",
  Code: "code",
} as const;

export type OrderShippingLineField =
  (typeof OrderShippingLineField)[keyof typeof OrderShippingLineField];

export const ORDER_SHIPPING_LINE_FIELDS = fieldValues(OrderShippingLineField);

export const OrderShippingLinePhysicalField = {
  ShippingLineId: "shipping_line_id",
  ShippingLineIndex: "shipping_line_index",
  OrderId: "order_id",
  TeamId: "team_id",
  PriceSetShopMoneyAmount: "price_set_shop_money_amount",
  PriceSetShopMoneyCurrencyCode: "price_set_shop_money_currency_code",
  DiscountedPriceSetShopMoneyAmount: "discounted_price_set_shop_money_amount",
  Title: "title",
  Code: "code",
} as const;

export type OrderShippingLinePhysicalField =
  (typeof OrderShippingLinePhysicalField)[keyof typeof OrderShippingLinePhysicalField];

export const ORDER_SHIPPING_LINE_PHYSICAL_FIELDS = fieldValues(
  OrderShippingLinePhysicalField,
);

export const OrderRefundLineItemField = {
  RefundLineItemId: "refund_line_item_id",
  OrderId: "order_id",
  TeamId: "team_id",
  RefundId: "refund_id",
  RefundIndex: "refund_index",
  RefundLineItemIndex: "refund_line_item_index",
  LineItemId: "line_item_id",
  Quantity: "quantity",
  Subtotal: "subtotal",
  TotalTax: "total_tax",
  UnitPrice: "unit_price",
  Vendor: "vendor",
  Sku: "sku",
  ProductTitle: "title",
  VariantTitle: "variant_title",
  ProductId: "product_id",
  VariantId: "variant_id",
  RestockType: "restock_type",
  RefundCreatedAt: "refund_created_at",
  RefundProcessedAt: "refund_processed_at",
} as const;

export type OrderRefundLineItemField =
  (typeof OrderRefundLineItemField)[keyof typeof OrderRefundLineItemField];

export const ORDER_REFUND_LINE_ITEM_FIELDS = fieldValues(
  OrderRefundLineItemField,
);

export const OrderRefundLineItemPhysicalField = {
  RefundLineItemId: "refund_line_item_id",
  OrderId: "order_id",
  TeamId: "team_id",
  RefundId: "refund_id",
  RefundIndex: "refund_index",
  RefundLineItemIndex: "refund_line_item_index",
  LineItemId: "line_item_id",
  Quantity: "quantity",
  Subtotal: "subtotal",
  TotalTax: "total_tax",
  UnitPrice: "unit_price",
  Vendor: "vendor",
  Sku: "sku",
  ProductTitle: "title",
  VariantTitle: "variant_title",
  ProductId: "product_id",
  VariantId: "variant_id",
  RestockType: "restock_type",
  RefundCreatedAt: "refund_created_at",
  RefundProcessedAt: "refund_processed_at",
} as const;

export type OrderRefundLineItemPhysicalField =
  (typeof OrderRefundLineItemPhysicalField)[keyof typeof OrderRefundLineItemPhysicalField];

export const ORDER_REFUND_LINE_ITEM_PHYSICAL_FIELDS = fieldValues(
  OrderRefundLineItemPhysicalField,
);

export function semanticFieldRef<TTable extends SemanticTableName>(
  table: TTable,
  field: SemanticFieldName<TTable>,
): SemanticFieldRef<TTable> {
  return { table, field };
}
